/**
 * Bilingual UI strings.
 *
 * Spanish is the primary working language: the lead strategist and much of the
 * field team operate in it, and District 8 is substantially Spanish-speaking.
 * English is a full peer, not a fallback — both dictionaries carry every key,
 * and the type system enforces that.
 *
 * Campaign-finance and election-law terms keep their English form in the
 * Spanish strings where that is the term of art staff will see on a filing
 * (for example "Texas Ethics Commission"). Translating those would make the
 * app disagree with the documents it is describing.
 */

export type Locale = "es" | "en";

const en = {
  "app.name": "Arturo CampaignOS",
  "app.tagline": "Arturo Alluin · El Paso City Council, District 8 · 2026",

  "nav.dashboard": "Dashboard",
  "nav.advisor": "Strategy advisor",
  "nav.evidence": "Evidence",
  "nav.work": "Work",
  "nav.finance": "Finance",
  "nav.filings": "Filings",
  "nav.contacts": "Contacts",
  "nav.issues": "Issues",
  "nav.decisions": "Decisions",
  "nav.risks": "Risks",
  "nav.vendors": "Vendors",
  "nav.claimsReview": "Claims review",
  "nav.report": "Report",
  "nav.admin": "Administration",
  "nav.memory": "Memory",
  "nav.warRoom": "War Room",
  "nav.watchdog": "Watchdog",
  "nav.field": "Field Map",
  "nav.calendar": "Calendar",
  "nav.board": "Board",
  "nav.projects": "Projects",
  "nav.settings": "Settings",
  "nav.guide": "Guide",
  "nav.console": "Service Console",
  "nav.hermes": "Hermes",
  "nav.signOut": "Sign out",
  "nav.language": "Language",

  "auth.title": "Sign in",
  "auth.subtitle": "Internal campaign system. Authorized staff only.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.submit": "Sign in",
  "auth.working": "Signing in…",
  "auth.failed": "Sign-in failed. Check your email and password.",
  "auth.noAccount":
    "No account record was found for you. Ask an administrator to provision access.",
  "auth.suspended": "This account is suspended. Contact an administrator.",
  "auth.wrongCampaign":
    "This account belongs to a different campaign. Arturo CampaignOS is exclusively for Arturo Alluin's El Paso City Council District 8 campaign.",

  "dash.greeting": "Good to see you, {name}",
  "dash.role": "Role",
  "dash.strategistTitle": "Strategy command center",
  "dash.evidenceBase": "Evidence base",
  "dash.approvedEvidence": "Approved and citable",
  "dash.pendingReview": "Awaiting review",
  "dash.pendingSources": "Registered, not yet inspected",
  "dash.recentAnswers": "Recent advisor answers",
  "dash.openFlags": "Open compliance flags",
  "dash.overdueTasks": "Overdue work",
  "dash.upcomingFilings": "Upcoming filings",
  "dash.filingLate": "Late",
  "dash.filingDueIn": "Due in {n}d",
  "dash.noAnswers": "No advisor answers yet.",
  "dash.researchGaps": "Outstanding research",
  "dash.researchGapsHelp":
    "Sources the campaign has registered but not yet inspected. The advisor will not cite these.",
  "dash.askAdvisor": "Ask the advisor",
  "dash.uploadEvidence": "Add evidence",
  "dash.issueCoverage": "Evidence by issue cluster",
  "dash.noCoverage": "No approved evidence is filed against any issue cluster yet.",
  "dash.commandCenter": "Command Center",
  "dash.daysToElection": "Days to election",
  "dash.daysToFiling": "Days to filing close",
  "dash.electionDay": "Election · November 3, 2026",
  "dash.filingClose": "Filing closes · August 17, 2026",
  "dash.filingClosed": "Filing closed",
  "dash.electionPassed": "Election day",
  "dash.days": "days",
  "dash.knowledge": "Knowledge items",
  "dash.mentions7d": "Mentions (7d)",
  "dash.watchdogPulse": "Public opinion pulse",
  "dash.sentiment": "Sentiment of tracked mentions",
  "dash.climbing": "Narratives climbing toward fact",
  "dash.breakoutAlert": "Breaking into news",
  "dash.nothingClimbing": "No narratives have broken into news coverage.",
  "dash.objectives": "Current objectives",
  "dash.noObjectives": "No objectives set. Set them in Memory → Working.",
  "dash.openWatchdog": "Open Watchdog",
  "dash.runSweep": "Run a watchdog sweep to populate the pulse.",

  "advisor.title": "Strategy advisor",
  "advisor.subtitle":
    "Answers are built only from approved evidence in this system. Every factual claim carries a citation, and claims that fail verification are removed before you see them.",
  "advisor.convertToTask": "Convert to task →",
  "advisor.taskAdded": "Added to Work",
  "advisor.question": "Question",
  "advisor.questionPlaceholder":
    "What does our evidence establish about downtown public safety in District 8?",
  "advisor.purpose": "Purpose",
  "advisor.purpose.research": "Research",
  "advisor.purpose.briefing": "Briefing",
  "advisor.purpose.operations": "Operations",
  "advisor.purpose.communications": "Communications",
  "advisor.purpose.field": "Field",
  "advisor.purpose.compliance_support": "Compliance support",
  "advisor.issueCluster": "Issue cluster",
  "advisor.allClusters": "All clusters",
  "advisor.ask": "Ask",
  "advisor.thinking": "Retrieving evidence and reasoning…",
  "advisor.summary": "Summary",
  "advisor.claims": "Claims",
  "advisor.sources": "Sources used",
  "advisor.modelAuthoredNote":
    "Written by the model, not verified against evidence. Treat as a research prompt, not a finding.",
  "advisor.gaps": "Evidence gaps",
  "advisor.required": "Sources that would close these gaps",
  "advisor.noAnswerTitle": "Not answerable from current evidence",
  "advisor.quarantined": "Removed by verification",
  "advisor.quarantinedHelp":
    "These claims failed mechanical citation checks and were withheld. They are shown for transparency, not for use.",
  "advisor.injection": "Prompt-injection attempt detected in source material",
  "advisor.verified": "Verified",
  "advisor.verifiedHelp": "Every claim resolved to supplied evidence.",
  "advisor.partial": "Partially verified",
  "advisor.partialHelp": "Some claims were withheld. See below.",
  "advisor.failed": "Failed verification",
  "advisor.outOfScope": "Out of scope",
  "advisor.noEvidence": "No evidence",
  "advisor.refused": "Declined",
  "advisor.provider": "Model",
  "advisor.excluded": "Evidence considered and excluded",
  "advisor.markReviewed": "Mark reviewed",
  "advisor.accepted": "Accept",
  "advisor.rejected": "Reject",
  "advisor.needsWork": "Needs work",
  "advisor.reviewSaved": "Review recorded.",
  "advisor.history": "Recent questions",

  "claim.fact": "Fact",
  "claim.allegation": "Allegation",
  "claim.response": "Response",
  "claim.inference": "Inference",
  "claim.recommendation": "Recommendation",
  "claim.confidence": "Confidence",
  "claim.high": "High",
  "claim.medium": "Medium",
  "claim.low": "Low",

  "reliability.primary": "Primary",
  "reliability.official_campaign": "Official campaign",
  "reliability.secondary": "Secondary",
  "reliability.anecdotal": "Anecdotal",
  "reliability.pending": "Not inspected",

  "aura.outcome.verified": "Verified",
  "aura.outcome.verified_with_quarantine": "Partially verified",
  "aura.outcome.failed_verification": "Failed verification",
  "aura.outcome.refused_on_retry": "Declined on retry",
  "aura.outcome.no_evidence": "No evidence",
  "aura.outcome.out_of_scope": "Out of scope",
  "aura.outcome.refused": "Declined",

  "compliance.rule.RELATED_PARTY": "Related-party payment",
  "compliance.rule.POSSIBLE_PERSONAL_USE": "Possible personal use",
  "compliance.rule.MEAL_WITHOUT_ATTENDEES": "Meal without attendees listed",
  "compliance.rule.FOREIGN_LOCATION": "Foreign location",
  "compliance.rule.MISSING_RECEIPT": "Missing receipt",
  "compliance.rule.POSSIBLE_DUPLICATE": "Possible duplicate",
  "compliance.rule.THIN_BUSINESS_PURPOSE": "Thin business purpose",

  "cluster.government_finance": "Government finance",
  "cluster.public_safety": "Public safety",
  "cluster.transportation": "Transportation",
  "cluster.housing": "Housing",
  "cluster.built_environment": "Built environment",
  "cluster.economic_development": "Economic development",
  "cluster.preservation": "Preservation",
  "cluster.neighborhood_services": "Neighborhood services",

  "mention.sourceType.news": "News",
  "mention.sourceType.blog": "Blog",
  "mention.sourceType.social": "Social",
  "mention.sourceType.official": "Official",
  "mention.sourceType.forum": "Forum",
  "mention.sourceType.other": "Other",

  "evidence.title": "Evidence",
  "evidence.subtitle":
    "Nothing here reaches the advisor until a person approves an excerpt for it.",
  "evidence.add": "Add evidence",
  "evidence.docTitle": "Title",
  "evidence.file": "File",
  "evidence.sourceType": "Source type",
  "evidence.publisher": "Publisher",
  "evidence.publishedDate": "Published date",
  "evidence.sourceUrl": "Source URL",
  "evidence.geography": "Geography",
  "evidence.clusters": "Issue clusters",
  "evidence.rights": "Rights and permission note",
  "evidence.upload": "Upload",
  "evidence.uploading": "Uploading… {percent}%",
  "evidence.extracting": "Extracting text. This can take a few minutes for audio and video.",
  "evidence.status": "Status",
  "evidence.status.awaiting_upload": "Awaiting upload",
  "evidence.status.pending_review": "Pending review",
  "evidence.status.approved": "Approved",
  "evidence.status.rejected": "Rejected",
  "evidence.extraction.pending": "Extraction queued",
  "evidence.extraction.running": "Extracting",
  "evidence.extraction.complete": "Extracted",
  "evidence.extraction.empty": "No text found",
  "evidence.extraction.failed": "Extraction failed",
  "evidence.empty": "No evidence has been added yet.",
  "evidence.search": "Filter by title",
  "evidence.review": "Review",
  "evidence.extractedText": "Extracted text",
  "evidence.warnings": "Extraction warnings",
  "evidence.proposeClaims": "Propose claims",
  "evidence.proposing": "Reading the document…",
  "evidence.proposedClaims": "Proposed claims",
  "evidence.quoteVerified": "Quote verified against document",
  "evidence.quoteUnverified": "Quote NOT found in document — do not rely on this",
  "evidence.approvedExcerpt": "Approved excerpt",
  "evidence.approvedExcerptHelp":
    "This exact text is what the advisor will read and cite. Paste it from the extracted text; do not rewrite it.",
  "evidence.useSuggested": "Use suggested excerpt",
  "evidence.reliability": "Reliability tier",
  "evidence.reliabilityNeedsApprover":
    "Primary and official-campaign tiers (and anything above secondary for OCR/transcripts) need a canonical approver.",
  "evidence.classification": "Classification",
  "evidence.officialPosition": "This is an official campaign position",
  "evidence.officialPositionHelp":
    "Only mark this when the campaign has formally adopted the content. It lets the advisor attribute positions to Arturo.",
  "evidence.approve": "Approve for citation",
  "evidence.reject": "Reject",
  "evidence.rejectReason": "Reason for rejection",
  "evidence.superseded": "This item has been superseded and is no longer available to the advisor.",
  "evidence.supersede": "Mark as superseded",
  "evidence.supersededBy": "Replacement evidence ID",
  "evidence.supersessionNote": "Supersession note",
  "evidence.supersessionSaved": "Supersession recorded. The older item remains in the audit archive.",
  "evidence.approved": "Approved. The advisor can now cite this.",
  "evidence.excerptNotInDoc":
    "Warning: this excerpt was not found verbatim in the extracted text. Approving it anyway will be recorded in the audit log.",
  "evidence.mediaKind": "Media",
  "evidence.modelDerived":
    "This text was produced by a model (OCR or transcription). Verify figures and names against the original before approving.",

  "work.title": "Work",
  "work.subtitle": "Tasks, owners, and deadlines.",
  "work.newTask": "New task",
  "work.taskTitle": "Task",
  "work.assignee": "Owner",
  "work.due": "Due",
  "work.status": "Status",
  "work.priority": "Priority",
  "work.status.todo": "To do",
  "work.status.in_progress": "In progress",
  "work.status.blocked": "Blocked",
  "work.status.done": "Done",
  "work.priority.low": "Low",
  "work.priority.normal": "Normal",
  "work.priority.high": "High",
  "work.priority.urgent": "Urgent",
  "work.empty": "No tasks yet.",
  "work.overdue": "Overdue",
  "work.unassigned": "Unassigned",
  "work.workload": "Open work by teammate",
  "work.create": "Create",

  "finance.title": "Finance and compliance",
  "finance.subtitle":
    "Compliance flags are deterministic rule output and decision support. They are not legal advice.",
  "finance.transactions": "Transactions",
  "finance.export": "Export CSV",
  "finance.submitExpense": "Submit expense",
  "finance.newVendor": "Add vendor",
  "finance.vendorName": "Vendor name",
  "finance.vendorCategory": "Vendor category",
  "finance.relatedParty": "Candidate, family, or related-party vendor",
  "finance.receipt": "Receipt evidence",
  "finance.receiptHelp": "Upload the receipt in Evidence before submitting the expense.",
  "finance.attendees": "Attendees",
  "finance.attendeesHelp": "Separate names with commas. Required documentation for meals.",
  "finance.country": "Country code",
  "finance.submitted": "Expense submitted for compliance screening.",
  "finance.vendorAdded": "Vendor added.",
  "finance.noVendors": "Add a vendor before submitting an expense.",
  "finance.noReceipts": "Upload receipt evidence before submitting an expense.",
  "finance.flags": "Compliance flags",
  "finance.amount": "Amount",
  "finance.vendor": "Vendor",
  "finance.category": "Category",
  "finance.date": "Date",
  "finance.purpose": "Business purpose",
  "finance.approvalStatus": "Status",
  "finance.status.pending_review": "Pending review",
  "finance.status.approved": "Approved",
  "finance.status.blocked_pending_review": "Blocked — high-severity flag",
  "finance.rule": "Rule",
  "finance.severity": "Severity",
  "finance.severity.high": "High",
  "finance.severity.medium": "Medium",
  "finance.severity.low": "Low",
  "finance.requiresCounsel": "Requires counsel review",
  "finance.resolve": "Resolve",
  "finance.resolution": "Resolution",
  "finance.resolution.cleared": "Cleared",
  "finance.resolution.corrected": "Corrected",
  "finance.resolution.escalated": "Escalated",
  "finance.resolution.amended_filing": "Amended filing",
  "finance.notes": "Notes",
  "finance.empty": "No transactions recorded.",
  "finance.noFlags": "No open compliance flags.",
  "finance.approve": "Approve",

  "filings.title": "Filings",
  "filings.subtitle": "Campaign-finance filing deadlines and status.",
  "filings.new": "New filing",
  "filings.name": "Filing",
  "filings.authority": "Authority",
  "filings.due": "Due",
  "filings.create": "Create",
  "filings.empty": "No filings tracked yet.",
  "filings.status": "Status",
  "filings.status.upcoming": "Upcoming",
  "filings.status.filed": "Filed",
  "filings.status.late": "Late",

  "report.title": "Weekly report",
  "report.subtitle": "A single printable snapshot — goals, open work, compliance, filings, and the watchdog pulse.",
  "report.print": "Print",
  "report.noneOpen": "Nothing open.",

  "claimsReview.title": "Claims review",
  "claimsReview.subtitle":
    "Atomic claims proposed from ingested documents, across all evidence — accept what belongs in the record, reject what doesn't.",
  "claimsReview.empty": "No claims awaiting review.",
  "claimsReview.accept": "Accept",
  "claimsReview.reject": "Reject",
  "claimsReview.viewEvidence": "View evidence",
  "claimsReview.quoteUnverified": "Quote not found verbatim in the source document",
  "claim.nature.established_fact": "Established fact",
  "claim.nature.reported_allegation": "Reported allegation",
  "claim.nature.subject_response": "Subject's response",
  "claim.nature.opinion": "Opinion",

  "contacts.title": "Contacts",
  "contacts.subtitle": "Voters, donors, volunteers, and press — one directory, with a logged interaction history.",
  "contacts.new": "Add contact",
  "contacts.name": "Name",
  "contacts.type": "Type",
  "contacts.phone": "Phone",
  "contacts.email": "Email",
  "contacts.notes": "Notes",
  "contacts.create": "Create",
  "contacts.empty": "No contacts yet.",
  "contacts.type.voter": "Voter",
  "contacts.type.donor": "Donor",
  "contacts.type.volunteer": "Volunteer",
  "contacts.type.press": "Press",
  "contacts.type.other": "Other",
  "contacts.export": "Export CSV",
  "contacts.interactions": "Interactions",
  "contacts.logInteraction": "Log interaction",
  "contacts.noInteractions": "No interactions logged yet.",
  "contacts.backToContacts": "Back to contacts",
  "interaction.type": "Type",
  "interaction.notes": "Notes",
  "interaction.log": "Log",
  "interaction.type.call": "Call",
  "interaction.type.text": "Text",
  "interaction.type.door": "Door knock",
  "interaction.type.email": "Email",
  "interaction.type.event": "Event",
  "interaction.type.other": "Other",

  "issues.title": "Issues",
  "issues.subtitle": "Open questions and blockers the team is tracking.",
  "issues.new": "New issue",
  "issues.description": "Description",
  "issues.create": "Create",
  "issues.empty": "No issues logged.",
  "issues.status": "Status",
  "issues.status.open": "Open",
  "issues.status.resolved": "Resolved",

  "decisions.title": "Decisions",
  "decisions.subtitle": "A record of what the campaign decided and why — so it doesn't have to be re-litigated later.",
  "decisions.new": "Record decision",
  "decisions.summary": "Decision",
  "decisions.rationale": "Rationale",
  "decisions.create": "Record",
  "decisions.empty": "No decisions recorded yet.",

  "risks.title": "Risk register",
  "risks.subtitle": "Named risks, how likely and how severe, and what mitigates each one.",
  "risks.new": "New risk",
  "risks.description": "Risk",
  "risks.severity": "Severity",
  "risks.likelihood": "Likelihood",
  "risks.mitigation": "Mitigation",
  "risks.create": "Create",
  "risks.empty": "No risks logged.",
  "risks.status": "Status",
  "risks.status.open": "Open",
  "risks.status.mitigated": "Mitigated",
  "risks.status.closed": "Closed",
  "risks.level.low": "Low",
  "risks.level.medium": "Medium",
  "risks.level.high": "High",

  "vendors.title": "Vendors",
  "vendors.subtitle": "Every vendor the campaign pays, and whether they're a related party.",
  "vendors.new": "Add vendor",
  "vendors.name": "Name",
  "vendors.category": "Category",
  "vendors.relatedParty": "Related party",
  "vendors.create": "Create",
  "vendors.empty": "No vendors yet.",

  "admin.forceSignOut": "End sessions",
  "admin.forceSignOutConfirm": "End all active sessions for this person? They'll need to sign in again.",
  "admin.forceSignOutDone": "Sessions ended.",

  "admin.title": "Administration",
  "admin.subtitle": "Accounts, roles, and the audit trail.",
  "admin.users": "Users",
  "admin.provision": "Provision user",
  "admin.setupLink": "One-time account setup link",
  "admin.setupLinkHelp": "Send this link securely to the user. It allows them to set their password.",
  "admin.copySetupLink": "Copy setup link",
  "admin.setupLinkCopied": "Setup link copied.",
  "admin.name": "Full name",
  "admin.email": "Email",
  "admin.roles": "Roles",
  "admin.locale": "Working language",
  "admin.jobTitle": "Title",
  "admin.effectivePermissions": "Effective permissions",
  "admin.allPermissions": "All permissions",
  "admin.editRoles": "Edit roles",
  "admin.doneEditing": "Done",
  "admin.noRoles": "No roles assigned",
  "admin.editingRolesFor": "Editing roles for {name}",
  "admin.suspend": "Suspend",
  "admin.reactivate": "Reactivate",
  "admin.audit": "Audit trail",
  "admin.action": "Action",
  "admin.actor": "Actor",
  "admin.when": "When",
  "admin.subject": "Subject",
  "admin.noAudit": "No audit events yet.",

  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.close": "Close",
  "common.loadMore": "Load more",
  "common.showing": "Showing {shown}",
  "common.showingOf": "Showing {shown} of {total}",
  "billing.checkoutFailed": "Checkout could not be opened.",
  "billing.activeTitle": "Campaign subscription active",
  "billing.activateTitle": "Activate the campaign command center",
  "billing.lede": "A one-time setup fee opens the command center; one monthly fee covers service, hosting, and AI through Election Day and any runoff. Cancel any time; access runs to the end of the paid month.",
  "billing.trialActive": "Trial active until {date} or $20 in AI usage, whichever comes first.",
  "billing.trialEnded": "The trial has ended. Activate the subscription to continue.",
  "billing.verifying": "Stripe returned successfully. We are verifying the signed payment; this screen will unlock automatically.",
  "billing.canceled": "Checkout was canceled; no charge was made.",
  "billing.feature1": "Access while the subscription is active",
  "billing.feature2": "Private, backed-up Arturo infrastructure",
  "billing.feature3": "Hermes, RAGFlow, and OpenViking included",
  "billing.feature4": "AI included in the monthly fee",
  "billing.accessThrough": "Access through {date}",
  "billing.maintenanceAccess": "Maintenance access",
  "billing.offer": "CAMPAIGN LAUNCH OFFER",
  "billing.timeLeft": "{days} days · {hours} hours",
  "billing.codeLabel": "ENTER AT STRIPE",
  "billing.deadline": "Save $750 on setup through September 30 at 11:59 PM El Paso time. One redemption.",
  "billing.once": "setup fee · then monthly",
  "billing.continue": "Continue to secure checkout",
  "billing.wrongAccount": "Signed in with the wrong account?",
  "billing.signOut": "Sign out",
  "billing.historyTitle": "Purchase history",
  "billing.historyEmpty": "No payments yet.",
  "billing.historyLaunch": "Setup + first month",
  "billing.historyMonthly": "Monthly service + AI",
  "billing.historyPaid": "Paid",
  "billing.historyRefunded": "Refunded",
  "billing.historyReceipt": "Receipt",
  "admin.aiLog": "AI output log",
  "admin.aiLogHint": "Every AI-generated output, as produced, with its model and a SHA-256 fingerprint. Append-only: nobody can edit or delete an entry.",
  "admin.aiLogEmpty": "No AI outputs recorded yet.",
  "admin.aiOperation": "Operation",
  "admin.aiModel": "Model",
  "admin.aiShow": "Show output",
  "admin.aiHide": "Hide",
  "billing.setupLabel": "ONE-TIME SETUP",
  "billing.monthlyLabel": "MONTHLY SERVICE + AI",
  "billing.perMonth": "/ month",
  "billing.firstCharge": "First charge today: {amount} (setup + first month).",
  "billing.canceledThrough": "Canceled. Access continues through {date}.",
  "billing.pastDue": "The last renewal failed. Update the card in Stripe before {date} to keep access.",
  "billing.includedLabel1": "SERVICE",
  "billing.included1": "Onboarding, the race analysis and playbook, and hands-on support from Elevate El Paso.",
  "billing.includedLabel2": "AI INCLUDED",
  "billing.included2": "AURA, the watchdog, OCR, and transcription are included in the monthly fee under a fair-use allowance.",
  "billing.includedLabel3": "SECURITY",
  "billing.included3": "Payments run on Stripe; the app never sees card details or holds a Stripe secret key.",
  "nav.strategy": "Analysis & strategy",
  "auth.emailOrUsername": "Email or username",
  "auth.resetEmailOnly": "Username accounts are reset by the campaign administrator.",
  "guide.startTour": "Start the guided tour",
  "pw.title": "Set your own password",
  "pw.lede": "Welcome, {name}. You signed in with a temporary password. Choose a new one to continue.",
  "pw.new": "New password",
  "pw.confirm": "Confirm new password",
  "pw.submit": "Save and continue",
  "pw.tooShort": "Use at least {n} characters.",
  "pw.mismatch": "The two passwords do not match.",
  "pw.relogin": "For security, sign out, sign back in with the temporary password, and try again.",
  "pw.failed": "The password could not be changed. Try again.",
  "welcome.eyebrow": "WELCOME",
  "welcome.title": "Welcome, {name}",
  "welcome.lede": "Two documents were prepared for you. Start with the race analysis, then read your suggested strategy. Take the tour of the whole app now, or later from the Guide.",
  "welcome.ledeGeneric": "Take a quick tour of what the app does, or explore on your own. The tour is always available from the Guide.",
  "welcome.raceAnalysis": "See the race analysis",
  "welcome.raceAnalysisHint": "The district, the voters, the issues, and all five candidates",
  "welcome.playbook": "See your suggested strategy",
  "welcome.playbookHint": "Message, platform, opponents, vulnerabilities, money, and calendar",
  "welcome.tour": "Take the tour",
  "welcome.explore": "Explore on my own",
  "tour.step": "TOUR · {n}/{total}",
  "tour.back": "Back",
  "tour.next": "Next",
  "tour.finish": "Finish",
  "tour.skip": "Skip tour",
  "tour.close": "Close",
  "strategy.title": "Analysis & strategy",
  "strategy.lede": "The District 8 race analysis and your campaign playbook, prepared by Elevate El Paso.",
  "strategy.raceAnalysis": "Race analysis",
  "strategy.playbook": "Campaign playbook",
  "strategy.contents": "Contents",
  "strategy.print": "Print",
  "strategy.missing": "This document has not been loaded yet.",
  "strategy.confidential": "Confidential. Visible only to the candidate and campaign leadership. Projections and assessments are the authors' opinion; facts are sourced in the race analysis.",
  "tour.dashboard.title": "Command center",
  "tour.dashboard.body": "Everything at a glance: the election clock, deadlines, open reviews, field activity, and narrative alerts, in priority order.",
  "tour.strategy.title": "Analysis & strategy",
  "tour.strategy.body": "Your race analysis and campaign playbook live here, always one click away. Share nothing from them publicly without checking the sources.",
  "tour.advisor.title": "AURA, the strategy advisor",
  "tour.advisor.body": "Ask any question about the race. AURA answers only from approved evidence, cites its sources, and separates facts from recommendations.",
  "tour.memory.title": "Institutional memory",
  "tour.memory.body": "Approved facts, decisions, playbook doctrine, and working notes the advisor draws on. Nothing enters without review.",
  "tour.evidence.title": "Evidence",
  "tour.evidence.body": "Every document, video, and article the campaign relies on: uploaded, transcribed, reviewed, and rated for reliability before AI can use it.",
  "tour.claims.title": "Claims review",
  "tour.claims.body": "Approve or reject individual facts extracted from evidence. Only approved claims support the advisor's answers.",
  "tour.watchdog.title": "Watchdog",
  "tour.watchdog.body": "Monitors public coverage of every candidate three times a day and shows which stories are climbing from chatter to news.",
  "tour.warRoom.title": "War room",
  "tour.warRoom.body": "The catalog of governed tools the campaign can run, and who may use each one.",
  "tour.field.title": "Field",
  "tour.field.body": "Live crew positions (with consent), shifts, and door-to-door work.",
  "tour.contacts.title": "Contacts",
  "tour.contacts.body": "The campaign CRM: supporters, donors, volunteers, and every interaction.",
  "tour.projects.title": "Projects and work",
  "tour.projects.body": "Goals, milestones, tasks, issues, decisions, and risks: the plan turned into assignments.",
  "tour.calendar.title": "Calendar",
  "tour.calendar.body": "Your events and the crew's, in one schedule.",
  "tour.board.title": "Team board",
  "tour.board.body": "Campaign-wide messaging with @mentions.",
  "tour.report.title": "Campaign brief",
  "tour.report.body": "A decision-ready report: objectives, deadlines, risks, compliance, and progress.",
  "tour.hermes.title": "Hermes",
  "tour.hermes.body": "A drafting assistant for supervised writing jobs; every output is reviewed before use.",
  "tour.finance.title": "Finance and compliance",
  "tour.finance.body": "Expenses, vendors, filings, and automatic compliance checks, so reporting is never a scramble.",
  "tour.guide.title": "Guide",
  "tour.guide.body": "Step-by-step instructions for every screen. Restart this tour from here any time.",
  "nav.billing": "License",
  "hermes.title": "Hermes workbench",
  "hermes.subtitle": "A supervised operations engine for research, planning, and drafting. Every result is a proposal until a person approves it.",
  "hermes.queue": "Work queue",
  "hermes.newRun": "Commission work",
  "hermes.instructions": "Context and constraints",
  "hermes.instructionsPlaceholder": "Include the decision, deadline, and sources it should use…",
  "hermes.run": "Queue for Hermes",
  "hermes.empty": "No Hermes work has been commissioned yet.",
  "hermes.offline": "Worker handoff",
  "hermes.offlineText": "Queued work is processed only when the private Hermes worker is online. CampaignOS never auto-approves host commands or publishes output.",
  "hermes.approve": "Approve proposal",
  "hermes.reject": "Reject",
  "hermes.cancel": "Request stop",
  "hermes.output": "Proposal output",
  "hermes.pending": "Awaiting human review",
  "hermes.type.morning_brief": "Morning brief",
  "hermes.type.research_refresh": "Research refresh",
  "hermes.type.task_triage": "Task triage",
  "hermes.type.source_validation": "Source validation",
  "hermes.type.meeting_prep": "Meeting prep",
  "hermes.type.bilingual_draft": "Bilingual draft",
  "common.loading": "Loading…",
  "common.error": "Something went wrong",
  "common.retry": "Retry",
  "common.none": "None",
  "common.required": "Required",
  "common.optional": "optional",
  "common.noPermission": "You do not have permission to view this.",
  "common.back": "Back",
  "search.open": "Search",
  "search.empty": "No results.",
  "search.kind.evidence": "Evidence",
  "search.kind.memory": "Memory",
  "search.kind.task": "Task",
  "search.kind.contact": "Contact",
  "common.of": "of",

  "memory.title": "Memory",
  "memory.subtitle":
    "Four tiers of campaign knowledge over the grounding floor. Facts still trace to evidence; strategy is kept separate and never treated as fact.",
  "memory.tier": "Tier",
  "memory.tier.semantic": "Semantic",
  "memory.tier.episodic": "Episodic",
  "memory.tier.procedural": "Procedural",
  "memory.tier.working": "Working",
  "memory.tier.semantic.help": "Durable, reviewed facts. Must cite evidence.",
  "memory.tier.episodic.help": "Dated events in the campaign timeline. Must cite evidence.",
  "memory.tier.procedural.help": "Strategy doctrine and playbooks. Used as recommendation, never as fact.",
  "memory.tier.working.help": "This session's objectives and focus. Context only.",
  "memory.add": "Add memory",
  "memory.statement": "Statement",
  "memory.rationale": "Rationale (why this works)",
  "memory.evidence": "Evidence IDs",
  "memory.evidenceHelp": "Comma-separated evidenceIds. Required for semantic and episodic.",
  "memory.occurredAt": "Occurred on",
  "memory.entities": "People / entities",
  "memory.clusters": "Issue clusters",
  "memory.confidence": "Confidence",
  "memory.create": "Save memory",
  "memory.status": "Status",
  "memory.status.proposed": "Proposed",
  "memory.status.approved": "Approved",
  "memory.status.retired": "Retired",
  "memory.approve": "Approve",
  "memory.retire": "Retire",
  "memory.empty": "No memories in this tier yet.",
  "memory.proposedNote": "Filed as proposed. It reaches the advisor once approved.",
  "memory.doctrineNote":
    "Doctrine is usable immediately, but the advisor presents it as recommendation, never as fact.",
  "memory.working.title": "Working memory — this session",
  "memory.working.objectives": "Objectives (one per line)",
  "memory.working.notes": "Notes",
  "memory.working.save": "Save working memory",
  "memory.working.saved": "Saved. The advisor will use this as current focus.",

  "war.title": "War Room",
  "war.subtitle":
    "The intelligence and analysis stack a campaign can stand up. Each tool below is described by what it does and how to use it defensibly — because for a campaign, how you run these matters as much as what they find.",
  "war.category.records": "Public-records intelligence",
  "war.category.discourse": "Public-discourse monitoring",
  "war.category.simulation": "Strategy simulation",
  "war.category.osint": "OSINT & footprint tooling",
  "war.category.knowledge": "Research & knowledge base",
  "war.what": "What it does",
  "war.use": "Campaign use",
  "war.guardrail": "Defensible-use note",
  "war.status.live": "Live",
  "war.status.recommended": "Recommended",
  "war.status.operatorRun": "Operator-run",
  "war.status.roadmap": "Roadmap",
  "war.status.caution": "Handle with counsel",
  "war.recordsIntro":
    "The defensible core. Everything here is public record — filings, votes, board memberships, published journalism — structured into the same cited evidence base the advisor reasons over. This is what actually wins arguments and survives scrutiny.",
  "war.osintIntro":
    "Powerful, dual-use, and the tools most likely to cause harm if pointed at private individuals. A campaign should run these only against itself (exposure audit) or with counsel, and never against journalists or private people. That governance is the differentiator, not the tools.",

  "war.tool.foxel.name": "Foxel",
  "war.tool.foxel.what": "Self-hosted media library with AI tagging and search over images, video, and documents.",
  "war.tool.foxel.use": "A private, searchable vault for every photo, clip, and scanned filing the campaign collects in the field — with nothing sent to a third party.",
  "war.tool.surfsense.name": "SurfSense",
  "war.tool.surfsense.what": "Private research assistant that answers questions over documents you feed it, with citations (a self-hosted NotebookLM).",
  "war.tool.surfsense.use": "Deep-read a report corpus without exposing it to a public AI service. Feeds candidate excerpts into the evidence base.",
  "war.tool.nocodb.name": "NocoDB",
  "war.tool.nocodb.what": "Open-source Airtable: a spreadsheet-database for structured records with a shareable UI.",
  "war.tool.nocodb.use": "Track donors, volunteers, turf, and vendor disclosures in structured tables that non-technical staff can edit.",
  "war.tool.spiderfoot.name": "SpiderFoot",
  "war.tool.spiderfoot.what": "Automation framework that gathers open-source intelligence about a domain, org, or digital footprint from 200+ sources.",
  "war.tool.spiderfoot.use": "Audit the campaign's own exposure: what is publicly discoverable about the candidate, staff, and infrastructure — before an opponent finds it.",
  "war.tool.reconng.name": "recon-ng",
  "war.tool.reconng.what": "Modular reconnaissance framework for open-source data gathering, similar in shape to a pentest console.",
  "war.tool.reconng.use": "Structured self-audit of the campaign's online surface. Operator-run under an engagement scope.",
  "war.tool.shodan.name": "Shodan",
  "war.tool.shodan.what": "Search engine for internet-connected devices and services.",
  "war.tool.shodan.use": "Check the campaign's own servers, cameras, and services for accidental public exposure.",
  "war.tool.sherlock.name": "Sherlock",
  "war.tool.sherlock.what": "Finds a username across hundreds of social platforms.",
  "war.tool.sherlock.use": "Brand and impersonation defense: find accounts squatting the candidate's name or impersonating the campaign.",
  "war.tool.ghunt.name": "GHunt",
  "war.tool.ghunt.what": "Investigates what a Google account exposes publicly.",
  "war.tool.ghunt.use": "Self-check what the campaign's own Google accounts leak. Not for use against private individuals.",
  "war.tool.spiderfoot.guard": "Run against your own assets under a written scope. Against third parties, only public-record facts belong in evidence.",
  "war.tool.people.guard": "Person-focused. Use only for the campaign's own exposure audit or brand defense, with counsel — never against journalists or private citizens.",
  "war.tool.betta.name": "Discourse Monitor (BettaFish-inspired)",
  "war.tool.betta.what": "Multi-agent monitoring of public political discourse, with sentiment analysis and an agent 'forum' that debates what the signal means.",
  "war.tool.betta.use": "Track how District issues move in public conversation and local media — labelled anecdotal, never as a poll. Adapted for US/local sources; the original targets Chinese platforms.",
  "war.tool.betta.guard": "Public posts only, reported as signal not measurement. No private groups, no covert profiling.",
  "war.tool.miro.name": "Message Simulator (MiroFish-inspired)",
  "war.tool.miro.what": "Agent-based simulation: model how District voter segments might react to a message before it ships — a synthetic focus group.",
  "war.tool.miro.use": "Pressure-test a reframe or an attack line against modeled segments, then test the winner with real people. Never a substitute for real voters.",
  "war.tool.miro.guard": "A rehearsal tool. Outputs are hypotheses to test, not findings — and never individualized targeting.",
  "war.tool.records.name": "Public-Records Desk",
  "war.tool.records.what": "Campaign finance filings, roll-call votes, board memberships, and published journalism, structured into the cited evidence base.",
  "war.tool.records.use": "The backbone of opposition and self research. 'Follow the money, follow the power' from documents anyone can pull — which is exactly why it survives scrutiny.",

  "watch.title": "Public Opinion Watchdog",
  "watch.subtitle":
    "An autonomous agent sweeps the public web three times a day for every mention of the District 8 field, and keeps the strategist supplied with fresh signal. This is anecdotal monitoring — what is being said and where attention moves — never a poll.",
  "watch.anecdotalBanner":
    "Everything here is anecdotal public chatter and coverage, not measurement. The advisor treats it as signal, never as fact, and never cites it.",
  "watch.runNow": "Run sweep now",
  "watch.running": "Sweeping the web…",
  "watch.lastRun": "Last sweep",
  "watch.never": "never",
  "watch.total": "Mentions tracked",
  "watch.last7d": "Last 7 days",
  "watch.last24h": "Last 24 hours",
  "watch.byEntity": "By figure",
  "watch.bySentiment": "Sentiment",
  "watch.bySource": "By source type",
  "watch.recent": "Recent mentions",
  "watch.empty": "No mentions gathered yet. Run a sweep to populate the board.",
  "watch.sentiment.positive": "Positive",
  "watch.sentiment.neutral": "Neutral",
  "watch.sentiment.negative": "Negative",
  "watch.runResult": "Sweep complete: {fresh} new mentions ({found} found).",
  "watch.schedule": "Automatic sweeps run at 8am, 1pm, and 7pm El Paso time.",
  "watch.lastRunFailed": "Last sweep failed — check the API key",

  "watch.meter": "Anecdotal Meter",
  "watch.meterHelp":
    "Each narrative's altitude on the reliability ladder, tracked over time. A topic that climbs from blog chatter into news is a breakout — the moment to respond.",
  "watch.meterEmpty": "No narratives tracked yet. They form as mentions accumulate across sweeps.",
  "watch.rung.social": "Social",
  "watch.rung.blog": "Blog",
  "watch.rung.local_news": "Local news",
  "watch.rung.established": "Established",
  "watch.rung.official_record": "Official record",
  "watch.momentum.rising": "Rising",
  "watch.momentum.steady": "Steady",
  "watch.momentum.falling": "Falling",
  "watch.breakout": "BREAKOUT to news",
  "watch.promotable": "At official record — eligible to promote to evidence",
  "watch.conduct": "Principal conduct alerts",
  "watch.conductHelp":
    "The candidate's own account engaging critics — his documented failure mode. Every one of these confirms the thin-skinned read and feeds the mockery. Standing rule: the candidate does not argue in comments; responses route through named staff.",
  "watch.conductNone": "No principal-conduct incidents flagged. Keep him out of the comments.",
  "watch.acknowledge": "Acknowledge",
  "dash.conductAlert": "Principal conduct alerts",
  "dash.conductAlertBody":
    "The candidate's own account has {n} flagged engagement(s) with critics. Get him out of the comments — this is the fastest available improvement to his standing.",

  "field.title": "Field Map",
  "field.subtitle":
    "Live positions of crew who are sharing, so the campaign can mobilize and route the candidate. Live only — no location history is kept, and each person controls their own sharing.",
  "field.share": "Share my location",
  "field.stop": "Stop sharing / end shift",
  "field.sharingOn": "You are sharing your live location with campaign managers. It stops the moment you end your shift or close this tab, and no history is kept.",
  "field.consentTitle": "Before you share",
  "field.consentBody":
    "Sharing shows campaign managers where you are while you work the streets, so they can coordinate and send the candidate to you. It is live only — nothing is recorded after you stop. You can stop anytime.",
  "field.shiftLabel": "Shift / turf label (optional)",
  "field.asCandidate": "Share as the candidate",
  "field.denied": "Location permission was denied. Enable it in your browser to share.",
  "field.unavailable": "Your device or browser can't provide location.",
  "field.acquiring": "Getting your location…",
  "field.crewOnline": "Crew sharing now",
  "field.noneOnline": "No one is sharing their location right now.",
  "field.lastUpdate": "Updated",
  "field.stale": "Stale",
  "field.you": "You",
  "field.candidate": "Candidate",
  "field.directions": "Directions",
  "field.recenter": "Recenter",
  "field.accuracy": "±{m} m",
  "field.mapUnavailable": "Map failed to load.",
  "field.managerHint": "Tap a marker for who it is, when it last updated, and directions.",
  "field.crewHint": "Only campaign managers can see the map. You only control your own sharing.",

  "cal.title": "Calendar",
  "cal.subtitle": "Candidate and crew scheduled events. Filing closes Aug 17; election is Nov 3.",
  "cal.add": "New event",
  "cal.eventTitle": "Event",
  "cal.start": "Starts",
  "cal.end": "Ends",
  "cal.location": "Location",
  "cal.type": "Type",
  "cal.type.canvass": "Canvass",
  "cal.type.fundraiser": "Fundraiser",
  "cal.type.media": "Media / interview",
  "cal.type.debate": "Debate / forum",
  "cal.type.community": "Community event",
  "cal.type.meeting": "Meeting",
  "cal.type.deadline": "Deadline",
  "cal.audience": "For",
  "cal.audience.all": "Whole campaign",
  "cal.audience.candidate": "Candidate",
  "cal.audience.crew": "Field crew",
  "cal.notes": "Notes",
  "cal.create": "Add to calendar",
  "cal.empty": "No events scheduled.",
  "cal.today": "Today",
  "cal.tomorrow": "Tomorrow",
  "cal.upcoming": "Upcoming",
  "cal.filterAll": "All",
  "cal.at": "at",

  "board.title": "Team Board",
  "board.subtitle": "Campaign chat. Tag teammates, and attach evidence, events, or tools to any message.",
  "board.channel.general": "general",
  "board.channel.field": "field",
  "board.channel.comms": "comms",
  "board.channel.strategy": "strategy",
  "board.compose": "Write a message… use @ to mention, or attach below",
  "board.send": "Send",
  "board.mention": "@ Mention",
  "board.attach": "# Attach",
  "board.mentionsMe": "Mentions me",
  "board.all": "All",
  "board.empty": "No messages yet. Start the conversation.",
  "board.attachEvidence": "Evidence",
  "board.attachEvent": "Event",
  "board.attachTool": "Tool",
  "board.pickUser": "Pick a teammate",
  "board.pickEvidence": "Pick evidence",
  "board.pickEvent": "Pick an event",
  "board.pickTool": "Pick a tool",
  "board.mentioning": "Mentioning",
  "board.attached": "Attached",

  "proj.title": "Projects & Strategy",
  "proj.subtitle":
    "Goals and key results drive the strategy; milestones set the timeline; the ideas board captures research before it becomes work. The best of a strategy tool and a project tool, in one place.",
  "proj.goals": "Goals & key results",
  "proj.goalsHelp": "What we are trying to achieve, and how we'll measure it.",
  "proj.addGoal": "New goal",
  "proj.objective": "Objective",
  "proj.why": "Why it matters",
  "proj.keyResults": "Key results (measurable)",
  "proj.krLabel": "Result",
  "proj.krTarget": "Target",
  "proj.krCurrent": "Current",
  "proj.addKr": "Add key result",
  "proj.goalProgress": "Progress",
  "proj.saveGoal": "Save goal",
  "proj.noGoals": "No goals yet. Set what winning looks like.",
  "proj.goal.active": "Active",
  "proj.goal.done": "Achieved",
  "proj.goal.paused": "Paused",
  "proj.milestones": "Milestones",
  "proj.milestonesHelp": "Dated checkpoints on the road to election day.",
  "proj.addMilestone": "New milestone",
  "proj.msTitle": "Milestone",
  "proj.due": "Due",
  "proj.noMilestones": "No milestones set.",
  "proj.ms.pending": "Pending",
  "proj.ms.done": "Done",
  "proj.ms.missed": "Missed",
  "proj.linkGoal": "Linked goal",
  "proj.none": "None",
  "proj.ideas": "Ideas & research board",
  "proj.ideasHelp": "Capture a strategy idea before it's a task. Move it right as it firms up.",
  "proj.addIdea": "New idea",
  "proj.promoteToGoal": "Promote to goal →",
  "proj.ideaTitle": "Idea",
  "proj.ideaDetail": "Detail",
  "proj.tags": "Tags",
  "proj.idea.new": "New",
  "proj.idea.researching": "Researching",
  "proj.idea.validated": "Validated",
  "proj.idea.parked": "Parked",
  "proj.noIdeas": "None here.",
  "proj.moveTo": "Move",
  "proj.markDone": "Mark achieved",

  "set.title": "Settings",
  "set.subtitle": "Appearance, your account, the user guide, and the setup tour.",
  "set.theme": "Color theme",
  "set.theme.system": "System",
  "set.theme.light": "Light",
  "set.theme.dark": "Dark",
  "set.language": "Language",
  "set.account": "Your account",
  "set.role": "Roles",
  "set.permissions": "Effective permissions",
  "set.guide": "User guide",
  "set.guideBody": "A walkthrough of every feature and workflow.",
  "set.guideOpen": "Open the guide",
  "set.tour": "Setup tour",
  "set.tourBody": "A short guided tour of the app.",
  "set.tourStart": "Start the tour",

  "asst.title": "Next best move",
  "asst.subtitle": "Suggestions from your live campaign data.",
  "asst.none": "You're on top of it — nothing urgent right now.",
  "asst.go": "Go",
  "asst.dismiss": "Dismiss",

  "wiz.skip": "Skip",
  "wiz.next": "Next",
  "wiz.back": "Back",
  "wiz.done": "Finish",
  "wiz.step": "Step {n} of {total}",
} as const;

export type StringKey = keyof typeof en;

const es: Record<StringKey, string> = {
  "app.name": "Arturo CampaignOS",
  "app.tagline": "Arturo Alluin · Concejo Municipal de El Paso, Distrito 8 · 2026",

  "nav.dashboard": "Panel",
  "nav.advisor": "Asesor de estrategia",
  "nav.evidence": "Evidencia",
  "nav.work": "Trabajo",
  "nav.finance": "Finanzas",
  "nav.filings": "Informes",
  "nav.contacts": "Contactos",
  "nav.issues": "Pendientes",
  "nav.decisions": "Decisiones",
  "nav.risks": "Riesgos",
  "nav.vendors": "Proveedores",
  "nav.claimsReview": "Revisión de afirmaciones",
  "nav.report": "Informe",
  "nav.admin": "Administración",
  "nav.memory": "Memoria",
  "nav.warRoom": "Sala de Guerra",
  "nav.watchdog": "Vigilante",
  "nav.field": "Mapa de Territorio",
  "nav.calendar": "Calendario",
  "nav.board": "Tablero",
  "nav.projects": "Proyectos",
  "nav.settings": "Ajustes",
  "nav.guide": "Guía",
  "nav.console": "Consola de Servicio",
  "nav.hermes": "Hermes",
  "nav.signOut": "Cerrar sesión",
  "nav.language": "Idioma",

  "auth.title": "Iniciar sesión",
  "auth.subtitle": "Sistema interno de campaña. Solo personal autorizado.",
  "auth.email": "Correo electrónico",
  "auth.password": "Contraseña",
  "auth.submit": "Entrar",
  "auth.working": "Entrando…",
  "auth.failed": "No se pudo iniciar sesión. Revisa tu correo y contraseña.",
  "auth.noAccount":
    "No existe un registro de usuario para ti. Pide a un administrador que te dé acceso.",
  "auth.suspended": "Esta cuenta está suspendida. Contacta a un administrador.",
  "auth.wrongCampaign":
    "Esta cuenta pertenece a otra campaña. Arturo CampaignOS es exclusivamente para la campaña de Arturo Alluin al Concejo Municipal de El Paso, Distrito 8.",

  "dash.greeting": "Qué gusto verte, {name}",
  "dash.role": "Rol",
  "dash.strategistTitle": "Centro de mando estratégico",
  "dash.evidenceBase": "Base de evidencia",
  "dash.approvedEvidence": "Aprobada y citable",
  "dash.pendingReview": "En espera de revisión",
  "dash.pendingSources": "Registrada, aún no inspeccionada",
  "dash.recentAnswers": "Respuestas recientes del asesor",
  "dash.openFlags": "Alertas de cumplimiento abiertas",
  "dash.overdueTasks": "Trabajo vencido",
  "dash.upcomingFilings": "Próximos informes",
  "dash.filingLate": "Atrasado",
  "dash.filingDueIn": "Vence en {n}d",
  "dash.noAnswers": "Aún no hay respuestas del asesor.",
  "dash.researchGaps": "Investigación pendiente",
  "dash.researchGapsHelp":
    "Fuentes que la campaña registró pero aún no ha inspeccionado. El asesor no las citará.",
  "dash.askAdvisor": "Consultar al asesor",
  "dash.uploadEvidence": "Agregar evidencia",
  "dash.issueCoverage": "Evidencia por eje temático",
  "dash.noCoverage": "Todavía no hay evidencia aprobada asignada a ningún eje temático.",
  "dash.commandCenter": "Centro de Mando",
  "dash.daysToElection": "Días para la elección",
  "dash.daysToFiling": "Días para el cierre de registro",
  "dash.electionDay": "Elección · 3 de noviembre de 2026",
  "dash.filingClose": "Registro cierra · 17 de agosto de 2026",
  "dash.filingClosed": "Registro cerrado",
  "dash.electionPassed": "Día de la elección",
  "dash.days": "días",
  "dash.knowledge": "Elementos de conocimiento",
  "dash.mentions7d": "Menciones (7d)",
  "dash.watchdogPulse": "Pulso de opinión pública",
  "dash.sentiment": "Sentimiento de las menciones rastreadas",
  "dash.climbing": "Narrativas subiendo hacia hecho",
  "dash.breakoutAlert": "Saltando a noticia",
  "dash.nothingClimbing": "Ninguna narrativa ha saltado a cobertura de noticias.",
  "dash.objectives": "Objetivos actuales",
  "dash.noObjectives": "Sin objetivos. Defínelos en Memoria → De trabajo.",
  "dash.openWatchdog": "Abrir Vigilante",
  "dash.runSweep": "Ejecuta un rastreo del vigilante para poblar el pulso.",

  "advisor.title": "Asesor de estrategia",
  "advisor.subtitle":
    "Las respuestas se construyen únicamente con evidencia aprobada en este sistema. Cada afirmación factual lleva su cita, y las afirmaciones que no pasan la verificación se eliminan antes de que las veas.",
  "advisor.convertToTask": "Convertir en tarea →",
  "advisor.taskAdded": "Agregada a Trabajo",
  "advisor.question": "Pregunta",
  "advisor.questionPlaceholder":
    "¿Qué establece nuestra evidencia sobre la seguridad pública en el centro del Distrito 8?",
  "advisor.purpose": "Propósito",
  "advisor.purpose.research": "Investigación",
  "advisor.purpose.briefing": "Informe",
  "advisor.purpose.operations": "Operaciones",
  "advisor.purpose.communications": "Comunicación",
  "advisor.purpose.field": "Territorio",
  "advisor.purpose.compliance_support": "Apoyo a cumplimiento",
  "advisor.issueCluster": "Eje temático",
  "advisor.allClusters": "Todos los ejes",
  "advisor.ask": "Preguntar",
  "advisor.thinking": "Recuperando evidencia y razonando…",
  "advisor.summary": "Resumen",
  "advisor.claims": "Afirmaciones",
  "advisor.sources": "Fuentes utilizadas",
  "advisor.modelAuthoredNote":
    "Redactado por el modelo; no verificado contra la evidencia. Trátalo como una pauta de investigación, no como un hallazgo.",
  "advisor.gaps": "Vacíos de evidencia",
  "advisor.required": "Fuentes que cerrarían estos vacíos",
  "advisor.noAnswerTitle": "No se puede responder con la evidencia actual",
  "advisor.quarantined": "Eliminado por la verificación",
  "advisor.quarantinedHelp":
    "Estas afirmaciones no pasaron la verificación mecánica de citas y fueron retenidas. Se muestran por transparencia, no para usarse.",
  "advisor.injection": "Se detectó un intento de inyección de instrucciones en el material fuente",
  "advisor.verified": "Verificado",
  "advisor.verifiedHelp": "Todas las afirmaciones se resolvieron contra la evidencia suministrada.",
  "advisor.partial": "Verificado parcialmente",
  "advisor.partialHelp": "Algunas afirmaciones fueron retenidas. Ver abajo.",
  "advisor.failed": "No pasó la verificación",
  "advisor.outOfScope": "Fuera de alcance",
  "advisor.noEvidence": "Sin evidencia",
  "advisor.refused": "Rechazado",
  "advisor.provider": "Modelo",
  "advisor.excluded": "Evidencia considerada y excluida",
  "advisor.markReviewed": "Marcar como revisada",
  "advisor.accepted": "Aceptar",
  "advisor.rejected": "Rechazar",
  "advisor.needsWork": "Requiere trabajo",
  "advisor.reviewSaved": "Revisión registrada.",
  "advisor.history": "Preguntas recientes",

  "claim.fact": "Hecho",
  "claim.allegation": "Señalamiento",
  "claim.response": "Respuesta",
  "claim.inference": "Inferencia",
  "claim.recommendation": "Recomendación",
  "claim.confidence": "Confianza",
  "claim.high": "Alta",
  "claim.medium": "Media",
  "claim.low": "Baja",

  "reliability.primary": "Primaria",
  "reliability.official_campaign": "Campaña oficial",
  "reliability.secondary": "Secundaria",
  "reliability.anecdotal": "Anecdótica",
  "reliability.pending": "Sin inspeccionar",

  "aura.outcome.verified": "Verificado",
  "aura.outcome.verified_with_quarantine": "Verificado parcialmente",
  "aura.outcome.failed_verification": "Verificación fallida",
  "aura.outcome.refused_on_retry": "Rechazada en el reintento",
  "aura.outcome.no_evidence": "Sin evidencia",
  "aura.outcome.out_of_scope": "Fuera de alcance",
  "aura.outcome.refused": "Rechazado",

  "compliance.rule.RELATED_PARTY": "Pago a parte relacionada",
  "compliance.rule.POSSIBLE_PERSONAL_USE": "Posible uso personal",
  "compliance.rule.MEAL_WITHOUT_ATTENDEES": "Comida sin lista de asistentes",
  "compliance.rule.FOREIGN_LOCATION": "Ubicación en el extranjero",
  "compliance.rule.MISSING_RECEIPT": "Falta comprobante",
  "compliance.rule.POSSIBLE_DUPLICATE": "Posible duplicado",
  "compliance.rule.THIN_BUSINESS_PURPOSE": "Propósito comercial insuficiente",

  "cluster.government_finance": "Finanzas de gobierno",
  "cluster.public_safety": "Seguridad pública",
  "cluster.transportation": "Transporte",
  "cluster.housing": "Vivienda",
  "cluster.built_environment": "Entorno construido",
  "cluster.economic_development": "Desarrollo económico",
  "cluster.preservation": "Preservación",
  "cluster.neighborhood_services": "Servicios de vecindario",

  "mention.sourceType.news": "Noticia",
  "mention.sourceType.blog": "Blog",
  "mention.sourceType.social": "Redes sociales",
  "mention.sourceType.official": "Oficial",
  "mention.sourceType.forum": "Foro",
  "mention.sourceType.other": "Otro",

  "evidence.title": "Evidencia",
  "evidence.subtitle":
    "Nada de esto llega al asesor hasta que una persona apruebe un extracto.",
  "evidence.add": "Agregar evidencia",
  "evidence.docTitle": "Título",
  "evidence.file": "Archivo",
  "evidence.sourceType": "Tipo de fuente",
  "evidence.publisher": "Editor o emisor",
  "evidence.publishedDate": "Fecha de publicación",
  "evidence.sourceUrl": "URL de la fuente",
  "evidence.geography": "Geografía",
  "evidence.clusters": "Ejes temáticos",
  "evidence.rights": "Nota de derechos y permisos",
  "evidence.upload": "Subir",
  "evidence.uploading": "Subiendo… {percent}%",
  "evidence.extracting":
    "Extrayendo texto. Puede tardar varios minutos en audio y video.",
  "evidence.status": "Estado",
  "evidence.status.awaiting_upload": "Esperando archivo",
  "evidence.status.pending_review": "Pendiente de revisión",
  "evidence.status.approved": "Aprobada",
  "evidence.status.rejected": "Rechazada",
  "evidence.extraction.pending": "Extracción en cola",
  "evidence.extraction.running": "Extrayendo",
  "evidence.extraction.complete": "Texto extraído",
  "evidence.extraction.empty": "No se encontró texto",
  "evidence.extraction.failed": "Falló la extracción",
  "evidence.empty": "Todavía no se ha agregado evidencia.",
  "evidence.search": "Filtrar por título",
  "evidence.review": "Revisar",
  "evidence.extractedText": "Texto extraído",
  "evidence.warnings": "Advertencias de extracción",
  "evidence.proposeClaims": "Proponer afirmaciones",
  "evidence.proposing": "Leyendo el documento…",
  "evidence.proposedClaims": "Afirmaciones propuestas",
  "evidence.quoteVerified": "Cita verificada contra el documento",
  "evidence.quoteUnverified":
    "La cita NO se encontró en el documento — no confíes en esta",
  "evidence.approvedExcerpt": "Extracto aprobado",
  "evidence.approvedExcerptHelp":
    "Este texto exacto es lo que el asesor leerá y citará. Pégalo desde el texto extraído; no lo reescribas.",
  "evidence.useSuggested": "Usar extracto sugerido",
  "evidence.reliability": "Nivel de confiabilidad",
  "evidence.reliabilityNeedsApprover":
    "Los niveles primario y oficial de campaña (y cualquier nivel superior a secundario para OCR/transcripciones) requieren un aprobador canónico.",
  "evidence.classification": "Clasificación",
  "evidence.officialPosition": "Esta es una posición oficial de la campaña",
  "evidence.officialPositionHelp":
    "Marca esto solo cuando la campaña haya adoptado formalmente el contenido. Permite al asesor atribuir posiciones a Arturo.",
  "evidence.approve": "Aprobar para citación",
  "evidence.reject": "Rechazar",
  "evidence.rejectReason": "Motivo del rechazo",
  "evidence.superseded": "Este elemento fue sustituido y ya no está disponible para el asesor.",
  "evidence.supersede": "Marcar como sustituido",
  "evidence.supersededBy": "ID de la evidencia de reemplazo",
  "evidence.supersessionNote": "Nota de sustitución",
  "evidence.supersessionSaved": "Sustitución registrada. El elemento anterior permanece en el archivo de auditoría.",
  "evidence.approved": "Aprobada. El asesor ya puede citarla.",
  "evidence.excerptNotInDoc":
    "Advertencia: este extracto no se encontró textualmente en el texto extraído. Aprobarlo de todas formas quedará registrado en la bitácora de auditoría.",
  "evidence.mediaKind": "Medio",
  "evidence.modelDerived":
    "Este texto fue producido por un modelo (OCR o transcripción). Verifica cifras y nombres contra el original antes de aprobar.",

  "work.title": "Trabajo",
  "work.subtitle": "Tareas, responsables y plazos.",
  "work.newTask": "Nueva tarea",
  "work.taskTitle": "Tarea",
  "work.assignee": "Responsable",
  "work.due": "Vence",
  "work.status": "Estado",
  "work.priority": "Prioridad",
  "work.status.todo": "Por hacer",
  "work.status.in_progress": "En curso",
  "work.status.blocked": "Bloqueada",
  "work.status.done": "Hecha",
  "work.priority.low": "Baja",
  "work.priority.normal": "Normal",
  "work.priority.high": "Alta",
  "work.priority.urgent": "Urgente",
  "work.empty": "Todavía no hay tareas.",
  "work.overdue": "Vencida",
  "work.unassigned": "Sin asignar",
  "work.workload": "Trabajo abierto por integrante",
  "work.create": "Crear",

  "finance.title": "Finanzas y cumplimiento",
  "finance.subtitle":
    "Las alertas de cumplimiento son resultado de reglas deterministas y sirven de apoyo a la decisión. No son asesoría legal.",
  "finance.transactions": "Transacciones",
  "finance.export": "Exportar CSV",
  "finance.submitExpense": "Enviar gasto",
  "finance.newVendor": "Agregar proveedor",
  "finance.vendorName": "Nombre del proveedor",
  "finance.vendorCategory": "Categoría del proveedor",
  "finance.relatedParty": "Proveedor del candidato, familiar o parte relacionada",
  "finance.receipt": "Evidencia del recibo",
  "finance.receiptHelp": "Sube el recibo en Evidencia antes de enviar el gasto.",
  "finance.attendees": "Asistentes",
  "finance.attendeesHelp": "Separa los nombres con comas. Es documentación requerida para comidas.",
  "finance.country": "Código de país",
  "finance.submitted": "Gasto enviado para revisión de cumplimiento.",
  "finance.vendorAdded": "Proveedor agregado.",
  "finance.noVendors": "Agrega un proveedor antes de enviar un gasto.",
  "finance.noReceipts": "Sube evidencia del recibo antes de enviar un gasto.",
  "finance.flags": "Alertas de cumplimiento",
  "finance.amount": "Monto",
  "finance.vendor": "Proveedor",
  "finance.category": "Categoría",
  "finance.date": "Fecha",
  "finance.purpose": "Propósito de campaña",
  "finance.approvalStatus": "Estado",
  "finance.status.pending_review": "Pendiente de revisión",
  "finance.status.approved": "Aprobada",
  "finance.status.blocked_pending_review": "Bloqueada — alerta de severidad alta",
  "finance.rule": "Regla",
  "finance.severity": "Severidad",
  "finance.severity.high": "Alta",
  "finance.severity.medium": "Media",
  "finance.severity.low": "Baja",
  "finance.requiresCounsel": "Requiere revisión legal",
  "finance.resolve": "Resolver",
  "finance.resolution": "Resolución",
  "finance.resolution.cleared": "Descartada",
  "finance.resolution.corrected": "Corregida",
  "finance.resolution.escalated": "Escalada",
  "finance.resolution.amended_filing": "Informe enmendado",
  "finance.notes": "Notas",
  "finance.empty": "No hay transacciones registradas.",
  "finance.noFlags": "No hay alertas de cumplimiento abiertas.",
  "finance.approve": "Aprobar",

  "filings.title": "Informes",
  "filings.subtitle": "Plazos y estado de los informes de finanzas de campaña.",
  "filings.new": "Nuevo informe",
  "filings.name": "Informe",
  "filings.authority": "Autoridad",
  "filings.due": "Vence",
  "filings.create": "Crear",
  "filings.empty": "Aún no hay informes registrados.",
  "filings.status": "Estado",
  "filings.status.upcoming": "Próximo",
  "filings.status.filed": "Presentado",
  "filings.status.late": "Atrasado",

  "report.title": "Informe semanal",
  "report.subtitle": "Una sola vista imprimible — objetivos, trabajo abierto, cumplimiento, informes y el pulso del vigilante.",
  "report.print": "Imprimir",
  "report.noneOpen": "Nada pendiente.",

  "claimsReview.title": "Revisión de afirmaciones",
  "claimsReview.subtitle":
    "Afirmaciones atómicas propuestas a partir de documentos ingeridos, de toda la evidencia — acepta lo que pertenece al registro, rechaza lo que no.",
  "claimsReview.empty": "No hay afirmaciones pendientes de revisión.",
  "claimsReview.accept": "Aceptar",
  "claimsReview.reject": "Rechazar",
  "claimsReview.viewEvidence": "Ver evidencia",
  "claimsReview.quoteUnverified": "La cita no se encontró de forma exacta en el documento fuente",
  "claim.nature.established_fact": "Hecho establecido",
  "claim.nature.reported_allegation": "Señalamiento reportado",
  "claim.nature.subject_response": "Respuesta del sujeto",
  "claim.nature.opinion": "Opinión",

  "contacts.title": "Contactos",
  "contacts.subtitle": "Votantes, donantes, voluntarios y prensa — un solo directorio, con historial de interacciones.",
  "contacts.new": "Agregar contacto",
  "contacts.name": "Nombre",
  "contacts.type": "Tipo",
  "contacts.phone": "Teléfono",
  "contacts.email": "Correo",
  "contacts.notes": "Notas",
  "contacts.create": "Crear",
  "contacts.empty": "Aún no hay contactos.",
  "contacts.type.voter": "Votante",
  "contacts.type.donor": "Donante",
  "contacts.type.volunteer": "Voluntario",
  "contacts.type.press": "Prensa",
  "contacts.type.other": "Otro",
  "contacts.export": "Exportar CSV",
  "contacts.interactions": "Interacciones",
  "contacts.logInteraction": "Registrar interacción",
  "contacts.noInteractions": "Aún no hay interacciones registradas.",
  "contacts.backToContacts": "Volver a contactos",
  "interaction.type": "Tipo",
  "interaction.notes": "Notas",
  "interaction.log": "Registrar",
  "interaction.type.call": "Llamada",
  "interaction.type.text": "Mensaje de texto",
  "interaction.type.door": "Visita a puerta",
  "interaction.type.email": "Correo",
  "interaction.type.event": "Evento",
  "interaction.type.other": "Otro",

  "issues.title": "Pendientes",
  "issues.subtitle": "Preguntas abiertas y bloqueos que el equipo está siguiendo.",
  "issues.new": "Nuevo pendiente",
  "issues.description": "Descripción",
  "issues.create": "Crear",
  "issues.empty": "No hay pendientes registrados.",
  "issues.status": "Estado",
  "issues.status.open": "Abierto",
  "issues.status.resolved": "Resuelto",

  "decisions.title": "Decisiones",
  "decisions.subtitle": "Un registro de lo que decidió la campaña y por qué — para no tener que volver a discutirlo después.",
  "decisions.new": "Registrar decisión",
  "decisions.summary": "Decisión",
  "decisions.rationale": "Justificación",
  "decisions.create": "Registrar",
  "decisions.empty": "Aún no hay decisiones registradas.",

  "risks.title": "Registro de riesgos",
  "risks.subtitle": "Riesgos identificados, su probabilidad, su severidad y qué los mitiga.",
  "risks.new": "Nuevo riesgo",
  "risks.description": "Riesgo",
  "risks.severity": "Severidad",
  "risks.likelihood": "Probabilidad",
  "risks.mitigation": "Mitigación",
  "risks.create": "Crear",
  "risks.empty": "No hay riesgos registrados.",
  "risks.status": "Estado",
  "risks.status.open": "Abierto",
  "risks.status.mitigated": "Mitigado",
  "risks.status.closed": "Cerrado",
  "risks.level.low": "Baja",
  "risks.level.medium": "Media",
  "risks.level.high": "Alta",

  "vendors.title": "Proveedores",
  "vendors.subtitle": "Cada proveedor al que paga la campaña, y si es parte relacionada.",
  "vendors.new": "Agregar proveedor",
  "vendors.name": "Nombre",
  "vendors.category": "Categoría",
  "vendors.relatedParty": "Parte relacionada",
  "vendors.create": "Crear",
  "vendors.empty": "Aún no hay proveedores.",

  "admin.forceSignOut": "Cerrar sesiones",
  "admin.forceSignOutConfirm": "¿Cerrar todas las sesiones activas de esta persona? Tendrá que iniciar sesión de nuevo.",
  "admin.forceSignOutDone": "Sesiones cerradas.",

  "admin.title": "Administración",
  "admin.subtitle": "Cuentas, roles y bitácora de auditoría.",
  "admin.users": "Usuarios",
  "admin.provision": "Dar de alta usuario",
  "admin.setupLink": "Enlace único para configurar la cuenta",
  "admin.setupLinkHelp": "Envía este enlace de forma segura al usuario. Le permite establecer su contraseña.",
  "admin.copySetupLink": "Copiar enlace",
  "admin.setupLinkCopied": "Enlace copiado.",
  "admin.name": "Nombre completo",
  "admin.email": "Correo electrónico",
  "admin.roles": "Roles",
  "admin.locale": "Idioma de trabajo",
  "admin.jobTitle": "Cargo",
  "admin.effectivePermissions": "Permisos efectivos",
  "admin.allPermissions": "Todos los permisos",
  "admin.editRoles": "Editar roles",
  "admin.doneEditing": "Listo",
  "admin.noRoles": "Sin roles asignados",
  "admin.editingRolesFor": "Editando roles de {name}",
  "admin.suspend": "Suspender",
  "admin.reactivate": "Reactivar",
  "admin.audit": "Bitácora de auditoría",
  "admin.action": "Acción",
  "admin.actor": "Autor",
  "admin.when": "Cuándo",
  "admin.subject": "Objeto",
  "admin.noAudit": "Todavía no hay eventos de auditoría.",

  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.close": "Cerrar",
  "common.loadMore": "Cargar más",
  "common.showing": "Mostrando {shown}",
  "common.showingOf": "Mostrando {shown} de {total}",
  "billing.checkoutFailed": "No se pudo abrir el pago.",
  "billing.activeTitle": "Suscripción de campaña activa",
  "billing.activateTitle": "Active el centro de mando",
  "billing.lede": "Una cuota única de instalación abre el centro de mando; una mensualidad cubre servicio, hospedaje e IA hasta el día de la elección y cualquier segunda vuelta. Cancele cuando quiera; el acceso sigue hasta el fin del mes pagado.",
  "billing.trialActive": "Prueba activa hasta {date} o hasta consumir $20 en IA, lo que ocurra primero.",
  "billing.trialEnded": "La prueba terminó. Active la suscripción para continuar.",
  "billing.verifying": "Stripe confirmó el regreso. Estamos verificando el pago firmado; esta pantalla se abrirá automáticamente.",
  "billing.canceled": "El pago fue cancelado; no se realizó ningún cargo.",
  "billing.feature1": "Acceso mientras la suscripción esté activa",
  "billing.feature2": "Infraestructura Arturo privada y respaldada",
  "billing.feature3": "Hermes, RAGFlow y OpenViking incluidos",
  "billing.feature4": "IA incluida en la mensualidad",
  "billing.accessThrough": "Acceso hasta {date}",
  "billing.maintenanceAccess": "Acceso de mantenimiento",
  "billing.offer": "OFERTA DE LANZAMIENTO",
  "billing.timeLeft": "{days} días · {hours} horas",
  "billing.codeLabel": "CÓDIGO EN STRIPE",
  "billing.deadline": "Ahorre $750 en la instalación hasta el 30 de septiembre a las 11:59 PM, hora de El Paso. Una redención.",
  "billing.once": "instalación · luego mensualidad",
  "billing.continue": "Continuar al pago seguro",
  "billing.wrongAccount": "¿Entró con la cuenta equivocada?",
  "billing.signOut": "Cerrar sesión",
  "billing.historyTitle": "Historial de compras",
  "billing.historyEmpty": "Todavía no hay pagos.",
  "billing.historyLaunch": "Instalación + primer mes",
  "billing.historyMonthly": "Servicio mensual + IA",
  "billing.historyPaid": "Pagado",
  "billing.historyRefunded": "Reembolsado",
  "billing.historyReceipt": "Recibo",
  "admin.aiLog": "Registro de resultados de IA",
  "admin.aiLogHint": "Cada resultado generado por IA, tal como se produjo, con su modelo y una huella SHA-256. Solo se agrega: nadie puede editar ni borrar una entrada.",
  "admin.aiLogEmpty": "Aún no hay resultados de IA registrados.",
  "admin.aiOperation": "Operación",
  "admin.aiModel": "Modelo",
  "admin.aiShow": "Ver resultado",
  "admin.aiHide": "Ocultar",
  "billing.setupLabel": "INSTALACIÓN ÚNICA",
  "billing.monthlyLabel": "SERVICIO MENSUAL + IA",
  "billing.perMonth": "/ mes",
  "billing.firstCharge": "Primer cargo hoy: {amount} (instalación + primer mes).",
  "billing.canceledThrough": "Cancelada. El acceso continúa hasta el {date}.",
  "billing.pastDue": "La última renovación falló. Actualice la tarjeta en Stripe antes del {date} para conservar el acceso.",
  "billing.includedLabel1": "SERVICIO",
  "billing.included1": "Incorporación, el análisis de la contienda y el manual, y acompañamiento directo de Elevate El Paso.",
  "billing.includedLabel2": "IA INCLUIDA",
  "billing.included2": "AURA, el vigilante, OCR y transcripción están incluidos en la mensualidad con un límite de uso razonable.",
  "billing.includedLabel3": "SEGURIDAD",
  "billing.included3": "Los pagos se hacen en Stripe; la aplicación nunca ve datos de tarjeta ni guarda una clave secreta de Stripe.",
  "nav.strategy": "Análisis y estrategia",
  "auth.emailOrUsername": "Correo o usuario",
  "auth.resetEmailOnly": "Las cuentas con usuario las restablece el administrador de la campaña.",
  "guide.startTour": "Iniciar el recorrido guiado",
  "pw.title": "Cree su propia contraseña",
  "pw.lede": "Bienvenido, {name}. Entró con una contraseña temporal. Elija una nueva para continuar.",
  "pw.new": "Contraseña nueva",
  "pw.confirm": "Confirme la contraseña nueva",
  "pw.submit": "Guardar y continuar",
  "pw.tooShort": "Use al menos {n} caracteres.",
  "pw.mismatch": "Las dos contraseñas no coinciden.",
  "pw.relogin": "Por seguridad, cierre sesión, vuelva a entrar con la contraseña temporal e inténtelo de nuevo.",
  "pw.failed": "No se pudo cambiar la contraseña. Inténtelo de nuevo.",
  "welcome.eyebrow": "BIENVENIDO",
  "welcome.title": "Bienvenido, {name}",
  "welcome.lede": "Preparamos dos documentos para usted. Empiece por el análisis de la contienda y luego lea su estrategia sugerida. Haga el recorrido por toda la aplicación ahora o después desde la Guía.",
  "welcome.ledeGeneric": "Haga un recorrido rápido por lo que hace la aplicación o explore por su cuenta. El recorrido siempre está disponible en la Guía.",
  "welcome.raceAnalysis": "Ver el análisis de la contienda",
  "welcome.raceAnalysisHint": "El distrito, los votantes, los temas y los cinco candidatos",
  "welcome.playbook": "Ver su estrategia sugerida",
  "welcome.playbookHint": "Mensaje, plataforma, oponentes, vulnerabilidades, dinero y calendario",
  "welcome.tour": "Hacer el recorrido",
  "welcome.explore": "Explorar por mi cuenta",
  "tour.step": "RECORRIDO · {n}/{total}",
  "tour.back": "Atrás",
  "tour.next": "Siguiente",
  "tour.finish": "Terminar",
  "tour.skip": "Saltar recorrido",
  "tour.close": "Cerrar",
  "strategy.title": "Análisis y estrategia",
  "strategy.lede": "El análisis de la contienda del Distrito 8 y el manual de su campaña, preparados por Elevate El Paso.",
  "strategy.raceAnalysis": "Análisis de la contienda",
  "strategy.playbook": "Manual de campaña",
  "strategy.contents": "Contenido",
  "strategy.print": "Imprimir",
  "strategy.missing": "Este documento todavía no se ha cargado.",
  "strategy.confidential": "Confidencial. Solo lo ven el candidato y la dirección de la campaña. Las proyecciones y valoraciones son opinión de los autores; los hechos tienen fuente en el análisis de la contienda.",
  "tour.dashboard.title": "Centro de mando",
  "tour.dashboard.body": "Todo de un vistazo: el reloj electoral, plazos, revisiones pendientes, actividad de campo y alertas de narrativa, en orden de prioridad.",
  "tour.strategy.title": "Análisis y estrategia",
  "tour.strategy.body": "Aquí están su análisis de la contienda y su manual de campaña, siempre a un clic. No comparta nada de ellos públicamente sin verificar las fuentes.",
  "tour.advisor.title": "AURA, el asesor estratégico",
  "tour.advisor.body": "Pregunte lo que quiera sobre la contienda. AURA responde solo con evidencia aprobada, cita sus fuentes y separa los hechos de las recomendaciones.",
  "tour.memory.title": "Memoria institucional",
  "tour.memory.body": "Hechos aprobados, decisiones, doctrina del manual y notas de trabajo que usa el asesor. Nada entra sin revisión.",
  "tour.evidence.title": "Evidencia",
  "tour.evidence.body": "Cada documento, video y artículo en que se apoya la campaña: cargado, transcrito, revisado y calificado por confiabilidad antes de que la IA lo use.",
  "tour.claims.title": "Revisión de afirmaciones",
  "tour.claims.body": "Apruebe o rechace los hechos extraídos de la evidencia. Solo las afirmaciones aprobadas respaldan las respuestas del asesor.",
  "tour.watchdog.title": "Vigilante",
  "tour.watchdog.body": "Monitorea la cobertura pública de cada candidato tres veces al día y muestra qué historias pasan de rumor a noticia.",
  "tour.warRoom.title": "Sala de guerra",
  "tour.warRoom.body": "El catálogo de herramientas controladas que la campaña puede usar y quién puede usar cada una.",
  "tour.field.title": "Campo",
  "tour.field.body": "Ubicación en vivo de los equipos (con consentimiento), turnos y trabajo puerta a puerta.",
  "tour.contacts.title": "Contactos",
  "tour.contacts.body": "El CRM de la campaña: simpatizantes, donantes, voluntarios y cada interacción.",
  "tour.projects.title": "Proyectos y trabajo",
  "tour.projects.body": "Metas, hitos, tareas, temas, decisiones y riesgos: el plan convertido en asignaciones.",
  "tour.calendar.title": "Calendario",
  "tour.calendar.body": "Sus eventos y los del equipo en una sola agenda.",
  "tour.board.title": "Tablero del equipo",
  "tour.board.body": "Mensajería de toda la campaña con @menciones.",
  "tour.report.title": "Informe de campaña",
  "tour.report.body": "Un informe listo para decidir: objetivos, plazos, riesgos, cumplimiento y avance.",
  "tour.hermes.title": "Hermes",
  "tour.hermes.body": "Asistente de redacción para trabajos supervisados; cada resultado se revisa antes de usarse.",
  "tour.finance.title": "Finanzas y cumplimiento",
  "tour.finance.body": "Gastos, proveedores, reportes y revisiones automáticas de cumplimiento, para que reportar nunca sea una carrera.",
  "tour.guide.title": "Guía",
  "tour.guide.body": "Instrucciones paso a paso para cada pantalla. Reinicie este recorrido desde aquí cuando quiera.",
  "nav.billing": "Licencia",
  "hermes.title": "Mesa de trabajo Hermes",
  "hermes.subtitle": "Motor supervisado para investigación, planeación y borradores. Todo resultado es una propuesta hasta que una persona lo apruebe.",
  "hermes.queue": "Cola de trabajo",
  "hermes.newRun": "Encargar trabajo",
  "hermes.instructions": "Contexto y restricciones",
  "hermes.instructionsPlaceholder": "Incluye la decisión, fecha límite y fuentes que debe usar…",
  "hermes.run": "Enviar a Hermes",
  "hermes.empty": "Todavía no se ha encargado trabajo a Hermes.",
  "hermes.offline": "Entrega al trabajador",
  "hermes.offlineText": "El trabajo en cola se procesa solo cuando el trabajador privado de Hermes está activo. CampaignOS nunca aprueba comandos del sistema ni publica resultados automáticamente.",
  "hermes.approve": "Aprobar propuesta",
  "hermes.reject": "Rechazar",
  "hermes.cancel": "Solicitar detener",
  "hermes.output": "Propuesta",
  "hermes.pending": "Revisión humana pendiente",
  "hermes.type.morning_brief": "Informe matutino",
  "hermes.type.research_refresh": "Actualizar investigación",
  "hermes.type.task_triage": "Priorizar tareas",
  "hermes.type.source_validation": "Validar fuentes",
  "hermes.type.meeting_prep": "Preparar reunión",
  "hermes.type.bilingual_draft": "Borrador bilingüe",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal",
  "common.retry": "Reintentar",
  "common.none": "Ninguno",
  "common.required": "Obligatorio",
  "common.optional": "opcional",
  "common.noPermission": "No tienes permiso para ver esto.",
  "common.back": "Volver",
  "search.open": "Buscar",
  "search.empty": "Sin resultados.",
  "search.kind.evidence": "Evidencia",
  "search.kind.memory": "Memoria",
  "search.kind.task": "Tarea",
  "search.kind.contact": "Contacto",
  "common.of": "de",

  "memory.title": "Memoria",
  "memory.subtitle":
    "Cuatro niveles de conocimiento de campaña sobre la base de fundamentación. Los hechos siguen anclados a la evidencia; la estrategia se mantiene aparte y nunca se trata como hecho.",
  "memory.tier": "Nivel",
  "memory.tier.semantic": "Semántica",
  "memory.tier.episodic": "Episódica",
  "memory.tier.procedural": "Procedimental",
  "memory.tier.working": "De trabajo",
  "memory.tier.semantic.help": "Hechos duraderos y revisados. Debe citar evidencia.",
  "memory.tier.episodic.help": "Eventos con fecha en la cronología. Debe citar evidencia.",
  "memory.tier.procedural.help": "Doctrina y manuales de estrategia. Se usa como recomendación, nunca como hecho.",
  "memory.tier.working.help": "Objetivos y enfoque de esta sesión. Solo contexto.",
  "memory.add": "Agregar memoria",
  "memory.statement": "Enunciado",
  "memory.rationale": "Fundamento (por qué funciona)",
  "memory.evidence": "IDs de evidencia",
  "memory.evidenceHelp": "evidenceIds separados por comas. Obligatorio para semántica y episódica.",
  "memory.occurredAt": "Ocurrió el",
  "memory.entities": "Personas / entidades",
  "memory.clusters": "Ejes temáticos",
  "memory.confidence": "Confianza",
  "memory.create": "Guardar memoria",
  "memory.status": "Estado",
  "memory.status.proposed": "Propuesta",
  "memory.status.approved": "Aprobada",
  "memory.status.retired": "Retirada",
  "memory.approve": "Aprobar",
  "memory.retire": "Retirar",
  "memory.empty": "Todavía no hay memorias en este nivel.",
  "memory.proposedNote": "Registrada como propuesta. Llega al asesor una vez aprobada.",
  "memory.doctrineNote":
    "La doctrina se puede usar de inmediato, pero el asesor la presenta como recomendación, nunca como hecho.",
  "memory.working.title": "Memoria de trabajo — esta sesión",
  "memory.working.objectives": "Objetivos (uno por línea)",
  "memory.working.notes": "Notas",
  "memory.working.save": "Guardar memoria de trabajo",
  "memory.working.saved": "Guardado. El asesor lo usará como enfoque actual.",

  "war.title": "Sala de Guerra",
  "war.subtitle":
    "El conjunto de herramientas de inteligencia y análisis que una campaña puede desplegar. Cada una se describe por lo que hace y cómo usarla de forma defendible — porque en una campaña, cómo las usas importa tanto como lo que encuentran.",
  "war.category.records": "Inteligencia de registros públicos",
  "war.category.discourse": "Monitoreo del discurso público",
  "war.category.simulation": "Simulación de estrategia",
  "war.category.osint": "Herramientas OSINT y de huella digital",
  "war.category.knowledge": "Investigación y base de conocimiento",
  "war.what": "Qué hace",
  "war.use": "Uso en campaña",
  "war.guardrail": "Nota de uso defendible",
  "war.status.live": "Activo",
  "war.status.recommended": "Recomendado",
  "war.status.operatorRun": "Operado por especialista",
  "war.status.roadmap": "En hoja de ruta",
  "war.status.caution": "Manejar con asesoría legal",
  "war.recordsIntro":
    "El núcleo defendible. Todo aquí es registro público — informes, votos, membresías de juntas, periodismo publicado — estructurado en la misma base de evidencia citada que usa el asesor. Esto es lo que gana los argumentos y resiste el escrutinio.",
  "war.osintIntro":
    "Potentes, de doble uso, y las herramientas con mayor riesgo si se apuntan a individuos privados. Una campaña debe usarlas solo contra sí misma (auditoría de exposición) o con asesoría legal, nunca contra periodistas o personas privadas. Esa gobernanza es el diferenciador, no las herramientas.",

  "war.tool.foxel.name": "Foxel",
  "war.tool.foxel.what": "Biblioteca multimedia autoalojada con etiquetado y búsqueda por IA sobre imágenes, video y documentos.",
  "war.tool.foxel.use": "Un archivo privado y buscable de cada foto, clip e informe escaneado que la campaña recoge en territorio — sin enviar nada a terceros.",
  "war.tool.surfsense.name": "SurfSense",
  "war.tool.surfsense.what": "Asistente de investigación privado que responde preguntas sobre tus documentos, con citas (un NotebookLM autoalojado).",
  "war.tool.surfsense.use": "Leer a fondo un corpus de informes sin exponerlo a un servicio de IA público. Alimenta extractos candidatos a la base de evidencia.",
  "war.tool.nocodb.name": "NocoDB",
  "war.tool.nocodb.what": "Airtable de código abierto: una base de datos tipo hoja de cálculo con interfaz compartible.",
  "war.tool.nocodb.use": "Registrar donantes, voluntarios, territorio y declaraciones de proveedores en tablas que el personal no técnico puede editar.",
  "war.tool.spiderfoot.name": "SpiderFoot",
  "war.tool.spiderfoot.what": "Marco de automatización que recopila inteligencia de fuentes abiertas sobre un dominio, organización o huella digital desde más de 200 fuentes.",
  "war.tool.spiderfoot.use": "Auditar la propia exposición de la campaña: qué es públicamente descubrible sobre el candidato, el equipo y la infraestructura — antes de que lo encuentre un rival.",
  "war.tool.reconng.name": "recon-ng",
  "war.tool.reconng.what": "Marco modular de reconocimiento para recopilación de datos de fuentes abiertas, con forma de consola de pentest.",
  "war.tool.reconng.use": "Autoauditoría estructurada de la superficie en línea de la campaña. Operado por especialista bajo un alcance definido.",
  "war.tool.shodan.name": "Shodan",
  "war.tool.shodan.what": "Motor de búsqueda de dispositivos y servicios conectados a internet.",
  "war.tool.shodan.use": "Revisar los propios servidores, cámaras y servicios de la campaña por exposición pública accidental.",
  "war.tool.sherlock.name": "Sherlock",
  "war.tool.sherlock.what": "Encuentra un nombre de usuario en cientos de plataformas sociales.",
  "war.tool.sherlock.use": "Defensa de marca y suplantación: encontrar cuentas que ocupan el nombre del candidato o suplantan a la campaña.",
  "war.tool.ghunt.name": "GHunt",
  "war.tool.ghunt.what": "Investiga qué expone públicamente una cuenta de Google.",
  "war.tool.ghunt.use": "Revisar qué filtran las propias cuentas de Google de la campaña. No para usar contra individuos privados.",
  "war.tool.spiderfoot.guard": "Ejecutar contra tus propios activos bajo un alcance escrito. Contra terceros, solo hechos de registro público entran a la evidencia.",
  "war.tool.people.guard": "Enfocadas en personas. Usar solo para la auditoría de exposición de la propia campaña o defensa de marca, con asesoría legal — nunca contra periodistas o ciudadanos privados.",
  "war.tool.betta.name": "Monitor de Discurso (inspirado en BettaFish)",
  "war.tool.betta.what": "Monitoreo multiagente del discurso político público, con análisis de sentimiento y un 'foro' de agentes que debate qué significa la señal.",
  "war.tool.betta.use": "Seguir cómo se mueven los temas del distrito en la conversación pública y los medios locales — etiquetado como anecdótico, nunca como encuesta. Adaptado a fuentes de EE. UU./locales; el original apunta a plataformas chinas.",
  "war.tool.betta.guard": "Solo publicaciones públicas, reportadas como señal, no como medición. Sin grupos privados, sin perfilado encubierto.",
  "war.tool.miro.name": "Simulador de Mensaje (inspirado en MiroFish)",
  "war.tool.miro.what": "Simulación basada en agentes: modelar cómo podrían reaccionar los segmentos de votantes del distrito a un mensaje antes de enviarlo — un grupo focal sintético.",
  "war.tool.miro.use": "Poner a prueba un reencuadre o una línea de ataque contra segmentos modelados, luego probar el ganador con personas reales. Nunca sustituye a los votantes reales.",
  "war.tool.miro.guard": "Una herramienta de ensayo. Los resultados son hipótesis a probar, no hallazgos — y nunca segmentación individualizada.",
  "war.tool.records.name": "Mesa de Registros Públicos",
  "war.tool.records.what": "Informes de financiamiento de campaña, votos nominales, membresías de juntas y periodismo publicado, estructurados en la base de evidencia citada.",
  "war.tool.records.use": "La columna vertebral de la investigación de oposición y propia. 'Sigue el dinero, sigue el poder' desde documentos que cualquiera puede obtener — que es justo por lo que resiste el escrutinio.",

  "watch.title": "Vigilante de Opinión Pública",
  "watch.subtitle":
    "Un agente autónomo rastrea la web pública tres veces al día en busca de cada mención del campo del Distrito 8, y mantiene al estratega con señal fresca. Es monitoreo anecdótico — qué se dice y hacia dónde se mueve la atención — nunca una encuesta.",
  "watch.anecdotalBanner":
    "Todo aquí es conversación y cobertura pública anecdótica, no medición. El asesor lo trata como señal, nunca como hecho, y nunca lo cita.",
  "watch.runNow": "Rastrear ahora",
  "watch.running": "Rastreando la web…",
  "watch.lastRun": "Último rastreo",
  "watch.never": "nunca",
  "watch.total": "Menciones rastreadas",
  "watch.last7d": "Últimos 7 días",
  "watch.last24h": "Últimas 24 horas",
  "watch.byEntity": "Por figura",
  "watch.bySentiment": "Sentimiento",
  "watch.bySource": "Por tipo de fuente",
  "watch.recent": "Menciones recientes",
  "watch.empty": "Aún no se han recopilado menciones. Ejecuta un rastreo para poblar el tablero.",
  "watch.sentiment.positive": "Positivo",
  "watch.sentiment.neutral": "Neutral",
  "watch.sentiment.negative": "Negativo",
  "watch.runResult": "Rastreo completo: {fresh} menciones nuevas ({found} encontradas).",
  "watch.schedule": "Los rastreos automáticos corren a las 8am, 1pm y 7pm, hora de El Paso.",
  "watch.lastRunFailed": "El último rastreo falló — revisa la clave de la API",

  "watch.meter": "Medidor Anecdótico",
  "watch.meterHelp":
    "La altitud de cada narrativa en la escala de fiabilidad, rastreada en el tiempo. Un tema que sube de rumor de blog a noticia es un salto — el momento de responder.",
  "watch.meterEmpty": "Aún no hay narrativas rastreadas. Se forman a medida que se acumulan menciones entre rastreos.",
  "watch.rung.social": "Redes",
  "watch.rung.blog": "Blog",
  "watch.rung.local_news": "Noticia local",
  "watch.rung.established": "Consolidada",
  "watch.rung.official_record": "Registro oficial",
  "watch.momentum.rising": "Subiendo",
  "watch.momentum.steady": "Estable",
  "watch.momentum.falling": "Bajando",
  "watch.breakout": "SALTO a noticia",
  "watch.promotable": "En registro oficial — apta para promover a evidencia",
  "watch.conduct": "Alertas de conducta del principal",
  "watch.conductHelp":
    "La cuenta propia del candidato interactuando con críticos — su punto débil documentado. Cada una confirma la lectura de piel fina y alimenta la burla. Regla permanente: el candidato no discute en comentarios; las respuestas van por personal nombrado.",
  "watch.conductNone": "No hay incidentes de conducta del principal. Mantenlo fuera de los comentarios.",
  "watch.acknowledge": "Reconocer",
  "dash.conductAlert": "Alertas de conducta del principal",
  "dash.conductAlertBody":
    "La cuenta propia del candidato tiene {n} interacción(es) marcada(s) con críticos. Sácalo de los comentarios — es la mejora más rápida disponible para su posición.",

  "field.title": "Mapa de Territorio",
  "field.subtitle":
    "Ubicaciones en vivo del equipo que está compartiendo, para que la campaña movilice y dirija al candidato. Solo en vivo — no se guarda historial de ubicación, y cada persona controla su propio uso.",
  "field.share": "Compartir mi ubicación",
  "field.stop": "Dejar de compartir / terminar turno",
  "field.sharingOn": "Estás compartiendo tu ubicación en vivo con los coordinadores. Se detiene en cuanto termines el turno o cierres esta pestaña, y no se guarda historial.",
  "field.consentTitle": "Antes de compartir",
  "field.consentBody":
    "Compartir muestra a los coordinadores dónde estás mientras trabajas las calles, para coordinar y enviarte al candidato. Es solo en vivo — nada se registra después de que te detengas. Puedes detenerte cuando quieras.",
  "field.shiftLabel": "Etiqueta de turno / territorio (opcional)",
  "field.asCandidate": "Compartir como el candidato",
  "field.denied": "Se denegó el permiso de ubicación. Actívalo en tu navegador para compartir.",
  "field.unavailable": "Tu dispositivo o navegador no puede dar ubicación.",
  "field.acquiring": "Obteniendo tu ubicación…",
  "field.crewOnline": "Equipo compartiendo ahora",
  "field.noneOnline": "Nadie está compartiendo su ubicación en este momento.",
  "field.lastUpdate": "Actualizado",
  "field.stale": "Sin señal",
  "field.you": "Tú",
  "field.candidate": "Candidato",
  "field.directions": "Cómo llegar",
  "field.recenter": "Recentrar",
  "field.accuracy": "±{m} m",
  "field.mapUnavailable": "El mapa no se pudo cargar.",
  "field.managerHint": "Toca un marcador para ver quién es, cuándo se actualizó y cómo llegar.",
  "field.crewHint": "Solo los coordinadores ven el mapa. Tú solo controlas tu propio uso compartido.",

  "cal.title": "Calendario",
  "cal.subtitle": "Eventos programados del candidato y del equipo. El registro cierra el 17 de agosto; la elección es el 3 de noviembre.",
  "cal.add": "Nuevo evento",
  "cal.eventTitle": "Evento",
  "cal.start": "Inicia",
  "cal.end": "Termina",
  "cal.location": "Lugar",
  "cal.type": "Tipo",
  "cal.type.canvass": "Recorrido",
  "cal.type.fundraiser": "Recaudación",
  "cal.type.media": "Medios / entrevista",
  "cal.type.debate": "Debate / foro",
  "cal.type.community": "Evento comunitario",
  "cal.type.meeting": "Reunión",
  "cal.type.deadline": "Fecha límite",
  "cal.audience": "Para",
  "cal.audience.all": "Toda la campaña",
  "cal.audience.candidate": "Candidato",
  "cal.audience.crew": "Equipo de territorio",
  "cal.notes": "Notas",
  "cal.create": "Agregar al calendario",
  "cal.empty": "No hay eventos programados.",
  "cal.today": "Hoy",
  "cal.tomorrow": "Mañana",
  "cal.upcoming": "Próximos",
  "cal.filterAll": "Todos",
  "cal.at": "a las",

  "board.title": "Tablero del Equipo",
  "board.subtitle": "Chat de campaña. Etiqueta a tus compañeros y adjunta evidencia, eventos o herramientas a cualquier mensaje.",
  "board.channel.general": "general",
  "board.channel.field": "territorio",
  "board.channel.comms": "comunicación",
  "board.channel.strategy": "estrategia",
  "board.compose": "Escribe un mensaje… usa @ para mencionar, o adjunta abajo",
  "board.send": "Enviar",
  "board.mention": "@ Mencionar",
  "board.attach": "# Adjuntar",
  "board.mentionsMe": "Me mencionan",
  "board.all": "Todos",
  "board.empty": "Aún no hay mensajes. Inicia la conversación.",
  "board.attachEvidence": "Evidencia",
  "board.attachEvent": "Evento",
  "board.attachTool": "Herramienta",
  "board.pickUser": "Elige un compañero",
  "board.pickEvidence": "Elige evidencia",
  "board.pickEvent": "Elige un evento",
  "board.pickTool": "Elige una herramienta",
  "board.mentioning": "Mencionando",
  "board.attached": "Adjunto",

  "proj.title": "Proyectos y Estrategia",
  "proj.subtitle":
    "Los objetivos y resultados clave guían la estrategia; los hitos marcan el calendario; el tablero de ideas captura la investigación antes de que se vuelva trabajo. Lo mejor de una herramienta de estrategia y una de proyectos, en un solo lugar.",
  "proj.goals": "Objetivos y resultados clave",
  "proj.goalsHelp": "Qué queremos lograr y cómo lo mediremos.",
  "proj.addGoal": "Nuevo objetivo",
  "proj.objective": "Objetivo",
  "proj.why": "Por qué importa",
  "proj.keyResults": "Resultados clave (medibles)",
  "proj.krLabel": "Resultado",
  "proj.krTarget": "Meta",
  "proj.krCurrent": "Actual",
  "proj.addKr": "Agregar resultado clave",
  "proj.goalProgress": "Progreso",
  "proj.saveGoal": "Guardar objetivo",
  "proj.noGoals": "Aún no hay objetivos. Define cómo se ve ganar.",
  "proj.goal.active": "Activo",
  "proj.goal.done": "Logrado",
  "proj.goal.paused": "En pausa",
  "proj.milestones": "Hitos",
  "proj.milestonesHelp": "Puntos de control con fecha rumbo al día de la elección.",
  "proj.addMilestone": "Nuevo hito",
  "proj.msTitle": "Hito",
  "proj.due": "Vence",
  "proj.noMilestones": "No hay hitos definidos.",
  "proj.ms.pending": "Pendiente",
  "proj.ms.done": "Hecho",
  "proj.ms.missed": "Vencido",
  "proj.linkGoal": "Objetivo vinculado",
  "proj.none": "Ninguno",
  "proj.ideas": "Tablero de ideas e investigación",
  "proj.ideasHelp": "Captura una idea de estrategia antes de que sea tarea. Muévela a la derecha conforme se concreta.",
  "proj.addIdea": "Nueva idea",
  "proj.promoteToGoal": "Convertir en objetivo →",
  "proj.ideaTitle": "Idea",
  "proj.ideaDetail": "Detalle",
  "proj.tags": "Etiquetas",
  "proj.idea.new": "Nueva",
  "proj.idea.researching": "Investigando",
  "proj.idea.validated": "Validada",
  "proj.idea.parked": "En pausa",
  "proj.noIdeas": "Nada aquí.",
  "proj.moveTo": "Mover",
  "proj.markDone": "Marcar logrado",

  "set.title": "Ajustes",
  "set.subtitle": "Apariencia, tu cuenta, la guía de usuario y el recorrido inicial.",
  "set.theme": "Tema de color",
  "set.theme.system": "Sistema",
  "set.theme.light": "Claro",
  "set.theme.dark": "Oscuro",
  "set.language": "Idioma",
  "set.account": "Tu cuenta",
  "set.role": "Roles",
  "set.permissions": "Permisos efectivos",
  "set.guide": "Guía de usuario",
  "set.guideBody": "Un recorrido por cada función y flujo de trabajo.",
  "set.guideOpen": "Abrir la guía",
  "set.tour": "Recorrido inicial",
  "set.tourBody": "Un breve recorrido guiado por la app.",
  "set.tourStart": "Iniciar el recorrido",

  "asst.title": "Mejor siguiente paso",
  "asst.subtitle": "Sugerencias a partir de los datos en vivo de tu campaña.",
  "asst.none": "Todo bajo control — nada urgente ahora mismo.",
  "asst.go": "Ir",
  "asst.dismiss": "Descartar",

  "wiz.skip": "Omitir",
  "wiz.next": "Siguiente",
  "wiz.back": "Atrás",
  "wiz.done": "Terminar",
  "wiz.step": "Paso {n} de {total}",
};

const DICTIONARIES: Record<Locale, Record<StringKey, string>> = { en, es };

export function translate(
  locale: Locale,
  key: StringKey,
  vars?: Record<string, string | number>,
): string {
  const template = DICTIONARIES[locale][key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Locale-correct dates and money, so "1.234,56" never shows up mislabelled. */
export function formatDate(locale: Locale, value: Date | null | undefined): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale === "es" ? "es-MX" : "en-US", {
    dateStyle: "medium",
  }).format(value);
}

export function formatDateTime(
  locale: Locale,
  value: Date | null | undefined,
): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale === "es" ? "es-MX" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

/**
 * Parses a `<input type="date">` value ("YYYY-MM-DD") as LOCAL midnight.
 *
 * `new Date("2026-08-15")` parses a date-only ISO string as UTC midnight per
 * spec. Displayed in any timezone west of UTC (El Paso is UTC-6/-7), that
 * lands on the previous day — a due date typed as the 15th shows as the 14th.
 * Building the Date from numeric parts uses the local timezone instead.
 */
export function parseDateInputLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/**
 * The calendar day a Date falls on, in the viewer's local timezone.
 *
 * `date.toISOString().slice(0, 10)` is UTC, so an evening event west of UTC
 * (e.g. 8pm Mountain Time) serializes to after-midnight UTC the NEXT day and
 * groups under the wrong date.
 */
export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Issue clusters are stored as their canonical English string (the data
 * contract every writer and every Firestore query uses, e.g. issueClusters:
 * ["Government finance"]) — that never changes. This only maps the stored
 * value to its display key, so the label can render in the viewer's language
 * without touching how the value is stored, filtered, or queried.
 */
export function clusterKey(cluster: string): StringKey {
  return `cluster.${cluster.toLowerCase().replace(/\s+/g, "_")}` as StringKey;
}

export function formatMoney(locale: Locale, cents: number): string {
  return new Intl.NumberFormat(locale === "es" ? "es-MX" : "en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
